import React, { useState, useEffect, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import {
  Calendar,
  Check,
  Clock,
  Video,
  Globe,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

// Import axios instance
import { axiosInstance } from "../../../utils/axios.js";

// Combine multiple availabilities - union of all available times
const combineAvailabilities = (availabilities) => {
  if (!availabilities || availabilities.length === 0) return null;
  if (availabilities.length === 1) return availabilities[0];

  // Use first availability as base
  const combined = { ...availabilities[0] };

  // Combine days - union of all available days
  const allDays = new Set();
  availabilities.forEach((av) => {
    if (av && av.days) av.days.forEach((d) => allDays.add(d));
  });
  combined.days = Array.from(allDays);

  // Combine timeSlots - union of all time ranges
  const allSlots = [];
  availabilities.forEach((av) => {
    if (av && av.timeSlots) {
      av.timeSlots.forEach((slot) => {
        if (
          !allSlots.find(
            (s) => s.timeStart === slot.timeStart && s.timeEnd === slot.timeEnd
          )
        ) {
          allSlots.push(slot);
        }
      });
    }
  });
  combined.timeSlots = allSlots.sort((a, b) =>
    a.timeStart.localeCompare(b.timeStart)
  );

  // Combine dayTimeSlots
  const daySlotMap = {};
  availabilities.forEach((av) => {
    if (av && av.dayTimeSlots) {
      av.dayTimeSlots.forEach((ds) => {
        if (!daySlotMap[ds.day]) daySlotMap[ds.day] = [];
        ds.slots.forEach((slot) => {
          if (
            !daySlotMap[ds.day].find(
              (s) =>
                s.timeStart === slot.timeStart && s.timeEnd === slot.timeEnd
            )
          ) {
            daySlotMap[ds.day].push(slot);
          }
        });
      });
    }
  });
  combined.dayTimeSlots = Object.entries(daySlotMap).map(([day, slots]) => ({
    day,
    slots: slots.sort((a, b) => a.timeStart.localeCompare(b.timeStart)),
  }));

  return combined;
};

function EventTypePreview() {
  const params = useParams();
  const location = useLocation();

  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [availableSlots, setAvailableSlots] = useState([]);
  const [eventTypeData, setEventTypeData] = useState(null);
  const [availabilityData, setAvailabilityData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeFormat, setTimeFormat] = useState("12h"); // 12h or 24h
  const [selectedTime, setSelectedTime] = useState(null);
  const [viewMode, setViewMode] = useState("month"); // month, week, day
  const [eventTypeId, setEventTypeId] = useState(null);
  const [actualEventTypeId, setActualEventTypeId] = useState(null);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    notes: "",
    timezone: "",
  });
  const [customQuestionAnswers, setCustomQuestionAnswers] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [existingBookings, setExistingBookings] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingLockId, setBookingLockId] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(5 * 60); // 5 minutes in seconds
  const [timerActive, setTimerActive] = useState(false);
  const [lockedSlots, setLockedSlots] = useState([]);
  const timerIntervalRef = useRef(null);
  const [userTimezone, setUserTimezone] = useState("");

  // OTP verification state
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [pendingBookingId, setPendingBookingId] = useState(null);
  const [otpError, setOtpError] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [attemptsRemaining, setAttemptsRemaining] = useState(3);
  const [pendingEmail, setPendingEmail] = useState(""); // Store email for OTP modal

  // Duration selection state
  const [selectedDuration, setSelectedDuration] = useState(null);

  // Team member selection state for team event types
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedTeamMember, setSelectedTeamMember] = useState(null);
  const [isTeamEvent, setIsTeamEvent] = useState(false);
  const [teamMembersLoading, setTeamMembersLoading] = useState(false);
  const [teamMembersAvailabilities, setTeamMembersAvailabilities] = useState(
    {}
  );
  const [combinedAvailability, setCombinedAvailability] = useState(null);

  // Recurring booking state
  const [recurringOccurrences, setRecurringOccurrences] = useState(1);
  const [showRecurringPreview, setShowRecurringPreview] = useState(false);
  const [recurringDates, setRecurringDates] = useState([]);
  const [originalAvailability, setOriginalAvailability] = useState(null);

  // Common styles with light theme
  const styles = {
    container: {
      minHeight: "100vh",
      backgroundColor: "#f5f5f5",
      color: "#333333",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "32px",
      fontFamily: "Arial, sans-serif",
    },
    card: {
      width: "1500px",
      maxWidth: "1300px",
      backgroundColor: "#ffffff",
      borderRadius: "12px",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
      overflow: "hidden",
    },
    flexRow: {
      display: "flex",
      flexDirection: "row",
      borderBottom: "1px solid #e5e7eb",
    },
    sidebar: {
      width: "25%",
      backgroundColor: "#ffffff",
      padding: "24px",
      borderRight: "1px solid #e5e7eb",
    },
    main: {
      width: "50%",
      padding: "24px",
      borderRight: "1px solid #e5e7eb",
    },
    timeline: {
      width: "25%",
      padding: "24px",
      backgroundColor: "#fafafa",
    },
    profileContainer: {
      display: "flex",
      alignItems: "center",
      marginBottom: "16px",
    },
    avatar: {
      width: "32px",
      height: "32px",
      backgroundColor: "#3b82f6",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#ffffff",
      fontSize: "14px",
      marginRight: "12px",
    },
    ownerName: {
      color: "#6b7280",
      fontSize: "14px",
    },
    eventTitle: {
      color: "#111827",
      fontSize: "20px",
      fontWeight: "bold",
    },
    eventDescription: {
      color: "#6b7280",
      fontSize: "14px",
    },
    detailsContainer: {
      marginTop: "32px",
    },
    detailItem: {
      display: "flex",
      alignItems: "center",
      marginBottom: "16px",
      fontSize: "14px",
      color: "#4b5563",
    },
    icon: {
      marginRight: "8px",
      color: "#6b7280",
    },
    headerRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "24px",
    },
    dateDisplay: {
      color: "#111827",
      fontSize: "18px",
      fontWeight: "500",
    },
    viewToggle: {
      display: "flex",
      border: "1px solid #e5e7eb",
      borderRadius: "6px",
      overflow: "hidden",
    },
    viewButton: {
      padding: "6px 12px",
      backgroundColor: "#ffffff",
      color: "#6b7280",
      border: "none",
      cursor: "pointer",
    },
    viewButtonActive: {
      backgroundColor: "#3b82f6",
      color: "#ffffff",
    },
    timeFormatToggle: {
      display: "flex",
      border: "1px solid #e5e7eb",
      borderRadius: "6px",
      overflow: "hidden",
      marginLeft: "8px",
    },
    timeFormatButton: {
      padding: "6px 12px",
      backgroundColor: "#ffffff",
      color: "#6b7280",
      border: "none",
      cursor: "pointer",
    },
    timeFormatButtonActive: {
      backgroundColor: "#f3f4f6",
      color: "#111827",
    },
    calendarContainer: {
      marginBottom: "24px",
    },
    timeSlotsContainer: {
      border: "1px solid #e5e7eb",
      borderRadius: "8px",
      padding: "16px",
      height: "400px",
      overflowY: "auto",
    },
    monthHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "16px",
    },
    monthTitle: {
      color: "#111827",
      fontSize: "16px",
      fontWeight: "500",
    },
    navButton: {
      backgroundColor: "#f9fafb",
      border: "1px solid #e5e7eb",
      cursor: "pointer",
      color: "#6b7280",
      width: "28px",
      height: "28px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "4px",
    },
    weekdaysGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(7, 1fr)",
      gap: "4px",
      textAlign: "center",
    },
    weekday: {
      color: "#6b7280",
      fontSize: "12px",
      fontWeight: "500",
      padding: "8px 0",
    },
    daysGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(7, 1fr)",
      gap: "4px",
    },
    dayCell: {
      height: "36px",
      position: "relative",
    },
    dayButton: {
      width: "100%",
      height: "100%",
      borderRadius: "4px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "14px",
      border: "none",
      background: "transparent",
      color: "#111827",
      cursor: "pointer",
    },
    dayButtonSelected: {
      backgroundColor: "#3b82f6",
      color: "#ffffff",
    },
    dayButtonToday: {
      border: "1px solid #3b82f6",
      fontWeight: "bold",
    },
    dayButtonUnavailable: {
      color: "#d1d5db",
      cursor: "not-allowed",
    },
    timeSlotsList: {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    },
    timeSlotButton: {
      width: "100%",
      padding: "10px 0",
      textAlign: "center",
      borderRadius: "4px",
      backgroundColor: "#f9fafb",
      color: "#111827",
      fontSize: "14px",
      border: "1px solid #e5e7eb",
      cursor: "pointer",
    },
    timeSlotButtonSelected: {
      backgroundColor: "#3b82f6",
      color: "#ffffff",
      border: "1px solid #3b82f6",
    },
    noSlots: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "100%",
      color: "#9ca3af",
    },
    timelineHeader: {
      marginBottom: "16px",
      fontSize: "16px",
      fontWeight: "500",
      color: "#111827",
    },
    timelineEvent: {
      padding: "12px",
      backgroundColor: "#f3f4f6",
      borderRadius: "6px",
      marginBottom: "12px",
    },
    timelineEventTime: {
      fontSize: "14px",
      fontWeight: "500",
      color: "#111827",
      marginBottom: "4px",
    },
    timelineEventTitle: {
      fontSize: "14px",
      color: "#4b5563",
    },
    loadingContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
      backgroundColor: "#f5f5f5",
    },
    spinner: {
      width: "48px",
      height: "48px",
      border: "4px solid rgba(59, 130, 246, 0.1)",
      borderTopColor: "#3b82f6",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
    },
    footer: {
      padding: "16px",
      borderTop: "1px solid #e5e7eb",
      textAlign: "center",
    },
    footerLink: {
      color: "#6b7280",
      fontSize: "14px",
      textDecoration: "none",
    },
    errorContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "40px",
      backgroundColor: "#fff",
      borderRadius: "8px",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
      maxWidth: "500px",
      margin: "100px auto",
    },
    errorTitle: {
      fontSize: "24px",
      fontWeight: "bold",
      color: "#ef4444",
      marginBottom: "16px",
    },
    errorMessage: {
      fontSize: "16px",
      color: "#4b5563",
      textAlign: "center",
      marginBottom: "20px",
    },
    errorButton: {
      padding: "8px 16px",
      backgroundColor: "#3b82f6",
      color: "#ffffff",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
      fontSize: "14px",
    },
    formInputLabel: {
      display: "block",
      marginBottom: "6px",
      fontSize: "14px",
      fontWeight: "500",
      color: "#4b5563",
    },
    formInput: {
      width: "100%",
      padding: "8px 12px",
      border: "1px solid #d1d5db",
      borderRadius: "4px",
      fontSize: "14px",
    },
    // Team member selection styles
    teamMemberSection: {
      marginTop: "24px",
      paddingTop: "16px",
      borderTop: "1px solid #e5e7eb",
    },
    teamMemberSectionTitle: {
      fontSize: "14px",
      fontWeight: "600",
      color: "#111827",
      marginBottom: "8px",
    },
    teamMemberHelperText: {
      fontSize: "12px",
      color: "#9ca3af",
      marginBottom: "12px",
    },
    teamMemberGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, 1fr)",
      gap: "8px",
    },
    teamMemberCard: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: "12px 8px",
      border: "1px solid #e5e7eb",
      borderRadius: "8px",
      cursor: "pointer",
      transition: "all 0.2s",
      backgroundColor: "#ffffff",
    },
    teamMemberCardSelected: {
      borderColor: "#3b82f6",
      backgroundColor: "#eff6ff",
    },
    teamMemberCardHover: {
      borderColor: "#93c5fd",
    },
    memberAvatar: {
      width: "40px",
      height: "40px",
      borderRadius: "50%",
      marginBottom: "8px",
      objectFit: "cover",
    },
    memberAvatarPlaceholder: {
      width: "40px",
      height: "40px",
      borderRadius: "50%",
      backgroundColor: "#3b82f6",
      color: "#ffffff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: "8px",
      fontSize: "16px",
      fontWeight: "bold",
    },
    anyAvailableIcon: {
      width: "40px",
      height: "40px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "24px",
      marginBottom: "8px",
      backgroundColor: "#f3f4f6",
      borderRadius: "50%",
    },
    teamMemberName: {
      fontSize: "12px",
      fontWeight: "500",
      color: "#374151",
      textAlign: "center",
    },
    formTextarea: {
      width: "100%",
      padding: "8px 12px",
      border: "1px solid #d1d5db",
      borderRadius: "4px",
      fontSize: "14px",
      minHeight: "100px",
      resize: "vertical",
    },
    formSuccess: {
      padding: "12px",
      backgroundColor: "#ecfdf5",
      color: "#047857",
      borderRadius: "4px",
      marginBottom: "16px",
    },
    modalButtonSecondary: {
      padding: "8px 16px",
      backgroundColor: "#f9fafb",
      color: "#4b5563",
      border: "1px solid #d1d5db",
      borderRadius: "4px",
      fontSize: "14px",
      cursor: "pointer",
    },
    modalButtonPrimary: {
      padding: "8px 16px",
      backgroundColor: "#3b82f6",
      color: "#ffffff",
      border: "none",
      borderRadius: "4px",
      fontSize: "14px",
      cursor: "pointer",
    },
    responsiveWrapper: {
      display: "flex",
      flexDirection: "row",
      width: "100%",
      "@media (max-width: 768px)": {
        flexDirection: "column",
      },
    },
    // New styles for loading state
    loadingOverlay: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(255, 255, 255, 0.8)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 10,
      borderRadius: "8px",
    },
    loadingSpinner: {
      width: "40px",
      height: "40px",
      border: "4px solid rgba(59, 130, 246, 0.1)",
      borderTopColor: "#3b82f6",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
    },
    loadingText: {
      marginLeft: "16px",
      fontSize: "16px",
      color: "#3b82f6",
      fontWeight: 500,
    },
    loadingMessage: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "column",
    },
    disabledButton: {
      backgroundColor: "#9cb3f5",
      cursor: "not-allowed",
    },
    successIcon: {
      marginBottom: "16px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "48px",
      height: "48px",
      backgroundColor: "#ecfdf5",
      borderRadius: "50%",
      color: "#10b981",
    },
    // New styles for timer
    timerContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: "16px",
      padding: "8px 16px",
      backgroundColor:
        timerActive && timeRemaining < 60 ? "#fee2e2" : "#f3f4f6",
      borderRadius: "4px",
      border:
        timerActive && timeRemaining < 60
          ? "1px solid #ef4444"
          : "1px solid #e5e7eb",
    },
    timerIcon: {
      marginRight: "8px",
      color: timerActive && timeRemaining < 60 ? "#ef4444" : "#6b7280",
    },
    timerText: {
      fontSize: "14px",
      fontWeight: "500",
      color: timerActive && timeRemaining < 60 ? "#ef4444" : "#6b7280",
    },
    timerWarning: {
      color: "#ef4444",
      fontWeight: "bold",
    },
    timeSlotButtonLocked: {
      backgroundColor: "#f3f4f6",
      color: "#9ca3af",
      borderColor: "#e5e7eb",
      cursor: "not-allowed",
    },
  };

  // Check for locked time slots
  const fetchLockedTimeSlots = async () => {
    if (!actualEventTypeId || !selectedDate) return;

    try {
      const formattedDate = selectedDate.format("YYYY-MM-DD");

      const response = await axiosInstance.post("/graphql", {
        query: `
        query GetLockedTimeSlots($eventTypeId: ID!, $date: String!) {
          getLockedTimeSlots(eventTypeId: $eventTypeId, date: $date) {
            timeStart
            lockId
            expiresAt
          }
        }
      `,
        variables: {
          eventTypeId: actualEventTypeId,
          date: formattedDate,
        },
      });

      if (response?.data?.data?.getLockedTimeSlots) {
        setLockedSlots(response.data.data.getLockedTimeSlots);
      } else if (response?.data?.getLockedTimeSlots) {
        setLockedSlots(response.data.getLockedTimeSlots);
      }
    } catch (error) {
      console.error("Error fetching locked time slots:", error);
    }
  };

  // Lock a time slot when selected
  const lockTimeSlot = async (timeSlot) => {
    try {
      const response = await axiosInstance.post("/graphql", {
        query: `
        mutation LockTimeSlot($input: LockTimeSlotInput!) {
          lockTimeSlot(input: $input) {
            lockId
            timeStart
            expiresAt
          }
        }
      `,
        variables: {
          input: {
            eventTypeId: actualEventTypeId,
            date: selectedDate.format("YYYY-MM-DD"),
            timeStart: timeSlot,
          },
        },
      });

      const lockData =
        response?.data?.data?.lockTimeSlot || response?.data?.lockTimeSlot;

      if (lockData) {
        setBookingLockId(lockData.lockId);

        // Calculate time remaining from expiresAt
        const expiresAt = new Date(lockData.expiresAt).getTime();
        const now = new Date().getTime();
        const remainingTime = Math.floor((expiresAt - now) / 1000);

        setTimeRemaining(remainingTime > 0 ? remainingTime : 5 * 60);
        setTimerActive(true);

        // Refresh locked slots
        fetchLockedTimeSlots();
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error locking time slot:", error);
      return false;
    }
  };

  // Release a time slot lock
  const releaseTimeSlotLock = async () => {
    if (!bookingLockId) return;

    try {
      await axiosInstance.post("/graphql", {
        query: `
        mutation ReleaseTimeSlotLock($lockId: ID!) {
          releaseTimeSlotLock(lockId: $lockId)
        }
      `,
        variables: {
          lockId: bookingLockId,
        },
      });

      setBookingLockId(null);
      setTimerActive(false);

      // Refresh locked slots
      fetchLockedTimeSlots();
    } catch (error) {
      console.error("Error releasing time slot lock:", error);
    }
  };

  // Format time for display
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
  };

  // Start countdown timer
  const startTimer = () => {
    // Clear any existing timer
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }

    setTimerActive(true);

    timerIntervalRef.current = setInterval(() => {
      setTimeRemaining((prevTime) => {
        if (prevTime <= 1) {
          // Time's up - clear interval and close modal
          clearInterval(timerIntervalRef.current);
          releaseTimeSlotLock();
          setOpenModal(false);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);
  };

  // Render the timer component
  const renderTimer = () => {
    if (!timerActive) return null;

    return (
      <div style={styles.timerContainer}>
        <br />
        <Clock size={16} style={styles.timerIcon} />
        <span style={styles.timerText}>
          Time remaining: {formatTime(timeRemaining)}
          {timeRemaining < 60 && (
            <span style={styles.timerWarning}> (Almost expired!)</span>
          )}
        </span>
      </div>
    );
  };

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, []);

  // Initialize recurring dates when event type data loads with recurring enabled
  useEffect(() => {
    if (eventTypeData?.recurring?.enabled && selectedDate) {
      const dates = calculateRecurringDates(
        selectedDate,
        eventTypeData.recurring.unit,
        eventTypeData.recurring.frequency,
        recurringOccurrences,
        availabilityData
      );
      setRecurringDates(dates);
    }
  }, [
    eventTypeData?.recurring?.enabled,
    selectedDate,
    recurringOccurrences,
    availabilityData,
  ]);

  // Generate time slots based on availability
  // beforeBufferOffset: minutes to offset the start time (for first slot of the day)
  function generateTimeSlots(
    timeStart,
    timeEnd,
    interval = 30,
    beforeBufferOffset = 0
  ) {
    const slots = [];
    let startTime;
    let endTime;

    // Handle 12-hour format strings like "09:00 am" or "06:00 pm"
    if (
      typeof timeStart === "string" &&
      (timeStart.includes("am") || timeStart.includes("pm"))
    ) {
      const [timePart, meridiem] = timeStart.split(" ");
      const [hours, minutes] = timePart.split(":").map(Number);
      let hour24 = hours;

      if (meridiem.toLowerCase() === "pm" && hours < 12) {
        hour24 = hours + 12;
      } else if (meridiem.toLowerCase() === "am" && hours === 12) {
        hour24 = 0;
      }

      startTime = dayjs().hour(hour24).minute(minutes).second(0);
    } else {
      startTime = dayjs(`2000-01-01 ${timeStart}`);
    }

    if (
      typeof timeEnd === "string" &&
      (timeEnd.includes("am") || timeEnd.includes("pm"))
    ) {
      const [timePart, meridiem] = timeEnd.split(" ");
      const [hours, minutes] = timePart.split(":").map(Number);
      let hour24 = hours;

      if (meridiem.toLowerCase() === "pm" && hours < 12) {
        hour24 = hours + 12;
      } else if (meridiem.toLowerCase() === "am" && hours === 12) {
        hour24 = 0;
      }

      endTime = dayjs().hour(hour24).minute(minutes).second(0);
    } else {
      endTime = dayjs(`2000-01-01 ${timeEnd}`);
    }

    // Apply before buffer offset to the start time
    // This ensures the first slot of the day has prep time before it
    if (beforeBufferOffset > 0) {
      startTime = startTime.add(beforeBufferOffset, "minute");
      console.log(
        `Applied before buffer offset: first slot starts at ${startTime.format(
          "h:mma"
        )} instead of original start time`
      );
    }

    let currentTime = startTime.clone();
    while (currentTime.isBefore(endTime)) {
      slots.push({
        timeStart: currentTime.format(timeFormat === "12h" ? "h:mma" : "HH:mm"),
        timeEnd: currentTime
          .add(interval, "minute")
          .format(timeFormat === "12h" ? "h:mma" : "HH:mm"),
      });
      // Move to the next slot
      currentTime = startTime.add(slots.length * interval, "minute");
    }

    return slots;
  }

  const fetchExistingBookings = async (date) => {
    if (!actualEventTypeId) return;

    try {
      const formattedDate = dayjs(date).format("YYYY-MM-DD");

      const response = await axiosInstance.post("/graphql", {
        query: `
        query GetExistingBookings($eventTypeId: ID!, $date: String!) {
          getBookingsForEventAndDate(eventTypeId: $eventTypeId, date: $date) {
            id
            date
            timeStart
            timeEnd
            status
          }
        }
      `,
        variables: {
          eventTypeId: actualEventTypeId,
          date: formattedDate,
        },
      });

      if (response?.data?.data?.getBookingsForEventAndDate) {
        const allBookings = response.data.data.getBookingsForEventAndDate;
        console.log("All fetched bookings:", allBookings);

        // Filter out canceled/rejected bookings - their time slots should be available
        // Only keep bookings with active statuses (or no status which means active)
        const activeBookings = allBookings.filter((b) => {
          const status = b.status?.toLowerCase();
          // Exclude canceled, cancelled, rejected statuses
          if (
            status === "canceled" ||
            status === "cancelled" ||
            status === "rejected"
          ) {
            console.log(`Excluding booking ${b.id} with status: ${b.status}`);
            return false;
          }
          return true;
        });
        console.log("Filtered active bookings:", activeBookings);
        setExistingBookings(activeBookings);
      } else if (response?.data?.getBookingsForEventAndDate) {
        const allBookings = response.data.getBookingsForEventAndDate;
        console.log("All fetched bookings:", allBookings);

        // Filter out canceled/rejected bookings - their time slots should be available
        const activeBookings = allBookings.filter((b) => {
          const status = b.status?.toLowerCase();
          if (
            status === "canceled" ||
            status === "cancelled" ||
            status === "rejected"
          ) {
            console.log(`Excluding booking ${b.id} with status: ${b.status}`);
            return false;
          }
          return true;
        });
        console.log("Filtered active bookings:", activeBookings);
        setExistingBookings(activeBookings);
      }
    } catch (error) {
      console.error("Error fetching existing bookings:", error);
    }
  };

  // Helper function to convert buffer time code to minutes
  function bufferCodeToMinutes(code) {
    const bufferMap = {
      false: 0,
      xs: 5,
      sm: 10,
      md: 15,
      lg: 20,
      xl: 30,
      "xl-45": 45,
      "xl-60": 60,
    };
    return bufferMap[code] || 0;
  }

  // Helper function to convert time slot interval code to minutes
  function intervalCodeToMinutes(code, defaultDuration) {
    if (code === "false" || !code) {
      return defaultDuration; // Use event duration as default
    }
    return bufferCodeToMinutes(code);
  }

  // Helper function to convert minimum notice to minutes
  function minimumNoticeToMinutes(minimumNotice) {
    if (!minimumNotice || !minimumNotice.value) return 0;
    const { value, unit } = minimumNotice;
    switch (unit) {
      case "Minutes":
        return value;
      case "Hours":
        return value * 60;
      case "Days":
        return value * 24 * 60;
      default:
        return value;
    }
  }

  // Helper function to calculate recurring dates (respects host availability)
  function calculateRecurringDates(
    startDate,
    unit,
    frequency,
    occurrences,
    availabilityData
  ) {
    const dates = [startDate.format("YYYY-MM-DD")];
    let current = startDate.clone();
    const maxIterations = occurrences * 30; // Safety limit to prevent infinite loops
    let iterations = 0;

    while (dates.length < occurrences && iterations < maxIterations) {
      iterations++;

      // Move to next potential date based on unit
      switch (unit) {
        case "Day":
          current = current.add(frequency, "day");
          break;
        case "Week":
          current = current.add(frequency, "week");
          break;
        case "Month":
          current = current.add(frequency, "month");
          break;
        case "Year":
          current = current.add(frequency, "year");
          break;
        default:
          current = current.add(frequency, "week");
      }

      // Check if this date is available
      const dayOfWeek = current.format("dddd"); // e.g., "Monday"
      const dateStr = current.format("YYYY-MM-DD");

      // Check if day of week is in available days
      const isDayAvailable =
        availabilityData?.days?.includes(dayOfWeek) ?? true;

      // Check for date-specific override (unavailable override)
      const override = availabilityData?.overrides?.find(
        (o) => dayjs(o.date).format("YYYY-MM-DD") === dateStr
      );
      const isBlockedByOverride = override?.isUnavailable === true;

      if (isDayAvailable && !isBlockedByOverride) {
        dates.push(dateStr);
      }
      // If not available, continue loop to find next available date
    }

    return dates;
  }

  function getAvailableSlots(availabilityData, selectedDate, eventDuration) {
    if (!availabilityData || !availabilityData.days) return [];

    const limits = eventTypeData?.limits;
    const dayOfWeek = selectedDate.format("dddd");
    const selectedDateStr = selectedDate.format("YYYY-MM-DD");

    // Debug: Log the limits data
    console.log("=== getAvailableSlots Debug ===");
    console.log("eventTypeData:", eventTypeData);
    console.log("limits:", limits);
    console.log("selectedDate:", selectedDateStr);

    // Determine the interval to use (from limits.timeSlotIntervals or event duration)
    const baseInterval = parseInt(eventDuration || 30);
    let interval = limits?.timeSlotIntervals
      ? intervalCodeToMinutes(limits.timeSlotIntervals, baseInterval)
      : baseInterval;

    console.log("timeSlotIntervals setting:", limits?.timeSlotIntervals);
    console.log("Using interval:", interval, "(base was:", baseInterval, ")");

    // Get buffer times
    const beforeBuffer = limits?.beforeEvent
      ? bufferCodeToMinutes(limits.beforeEvent)
      : 0;
    const afterBuffer = limits?.afterEvent
      ? bufferCodeToMinutes(limits.afterEvent)
      : 0;

    console.log(
      "beforeEvent setting:",
      limits?.beforeEvent,
      "-> buffer:",
      beforeBuffer,
      "minutes"
    );
    console.log(
      "afterEvent setting:",
      limits?.afterEvent,
      "-> buffer:",
      afterBuffer,
      "minutes"
    );

    // Adjust interval to account for buffer times
    // The minimum spacing between slots should be: eventDuration + beforeBuffer + afterBuffer
    // This ensures consecutive bookings don't have overlapping buffer periods
    // Example: 30 min event + 5 min before + 5 min after = 40 min minimum interval
    if (beforeBuffer > 0 || afterBuffer > 0) {
      const minInterval = baseInterval + beforeBuffer + afterBuffer;
      if (interval < minInterval) {
        console.log(
          `Adjusting interval from ${interval} to ${minInterval} to account for buffers (before: ${beforeBuffer}, after: ${afterBuffer})`
        );
        interval = minInterval;
      }
    }

    // Get minimum notice in minutes
    const minimumNoticeMinutes = limits?.minimumNotice
      ? minimumNoticeToMinutes(limits.minimumNotice)
      : 0;

    console.log(
      "minimumNotice setting:",
      limits?.minimumNotice,
      "-> minutes:",
      minimumNoticeMinutes
    );
    console.log("limitFirstSlotOnly:", limits?.limitFirstSlotOnly);
    console.log("limitFutureBookings:", limits?.limitFutureBookings);
    console.log("offsetStartTimes:", limits?.offsetStartTimes);

    // Check for future booking limits
    const now = dayjs();
    const today = dayjs().startOf("day");

    // Apply limit future bookings setting
    if (limits?.limitFutureBookings?.enabled) {
      console.log("=== Limit Future Bookings ===");
      console.log("limitFutureBookings config:", limits.limitFutureBookings);

      if (limits.limitFutureBookings.limitByDays) {
        // Limit by days
        const daysValue = limits.limitFutureBookings.days?.value || 30;
        const daysUnit =
          limits.limitFutureBookings.days?.unit || "Calendar Days";

        console.log(`Limit by days: ${daysValue} ${daysUnit}`);

        let maxDate;
        if (daysUnit === "Business Days") {
          // Calculate business days (excluding weekends)
          let businessDays = 0;
          let checkDate = today.clone();
          while (businessDays < daysValue) {
            checkDate = checkDate.add(1, "day");
            const dayOfWeek = checkDate.day();
            if (dayOfWeek !== 0 && dayOfWeek !== 6) {
              // Not Sunday (0) or Saturday (6)
              businessDays++;
            }
          }
          maxDate = checkDate;
        } else {
          // Calendar days
          maxDate = today.add(daysValue, "day");
        }

        console.log("Max bookable date:", maxDate.format("YYYY-MM-DD"));
        console.log("Selected date:", selectedDate.format("YYYY-MM-DD"));

        // If selected date is beyond the limit, return empty
        if (selectedDate.isAfter(maxDate, "day")) {
          console.log(
            "Date is beyond future booking limit - returning no slots"
          );
          return [];
        }
      } else {
        // Limit by date range
        const fromDate = limits.limitFutureBookings.dateRange?.fromDate
          ? dayjs(limits.limitFutureBookings.dateRange.fromDate)
          : null;
        const toDate = limits.limitFutureBookings.dateRange?.toDate
          ? dayjs(limits.limitFutureBookings.dateRange.toDate)
          : null;

        if (fromDate && selectedDate.isBefore(fromDate, "day")) {
          return [];
        }
        if (toDate && selectedDate.isAfter(toDate, "day")) {
          return [];
        }
      }
    }

    // Check for an override
    const override = availabilityData.overrides?.find(
      (o) => dayjs(o.date).format("YYYY-MM-DD") === selectedDateStr
    );

    let timeSlots = [];

    if (override && !override.isUnavailable && override.timeSlots?.length > 0) {
      // Generate time slots for each entry in override.timeSlots
      // Apply before buffer only to the first time range of the day
      override.timeSlots.forEach((slot, index) => {
        const slots = generateTimeSlots(
          slot.timeStart,
          slot.timeEnd,
          interval,
          index === 0 ? beforeBuffer : 0 // Only apply before buffer to first range
        );
        timeSlots = [...timeSlots, ...slots];
      });
    } else if (availabilityData.days?.includes(dayOfWeek)) {
      // First, check if dayTimeSlots is available (new structure with multiple slots per day)
      const dayTimeSlotEntry = availabilityData.dayTimeSlots?.find(
        (dts) => dts.day === dayOfWeek
      );

      if (dayTimeSlotEntry && dayTimeSlotEntry.slots?.length > 0) {
        // Use all slots from dayTimeSlots for this specific day
        console.log(
          `Using dayTimeSlots for ${dayOfWeek}:`,
          dayTimeSlotEntry.slots
        );
        // Apply before buffer only to the first time range of the day
        dayTimeSlotEntry.slots.forEach((slot, index) => {
          const slots = generateTimeSlots(
            slot.timeStart,
            slot.timeEnd,
            interval,
            index === 0 ? beforeBuffer : 0 // Only apply before buffer to first range
          );
          timeSlots = [...timeSlots, ...slots];
        });
      } else if (availabilityData.timeSlots?.length > 0) {
        // Fallback to old timeSlots structure (single slot per day or shared slots)
        // Generate time slots for each entry in the timeSlots array
        console.log(
          `Using legacy timeSlots for ${dayOfWeek}:`,
          availabilityData.timeSlots
        );
        // Apply before buffer only to the first time range of the day
        availabilityData.timeSlots.forEach((slot, index) => {
          const slots = generateTimeSlots(
            slot.timeStart,
            slot.timeEnd,
            interval,
            index === 0 ? beforeBuffer : 0 // Only apply before buffer to first range
          );
          timeSlots = [...timeSlots, ...slots];
        });
      }
    }

    // Apply offset start times if enabled
    if (limits?.offsetStartTimes?.enabled && limits.offsetStartTimes.value) {
      const offsetMinutes = limits.offsetStartTimes.value;
      timeSlots = timeSlots.map((slot) => {
        // Parse the time and add offset
        const timeStr = slot.timeStart;
        let slotTime;

        if (timeStr.includes("am") || timeStr.includes("pm")) {
          const [timePart, meridiem] = timeStr.split(/(?=[ap]m)/i);
          const [hours, minutes] = timePart.split(":").map(Number);
          let hour24 = hours;
          if (meridiem.toLowerCase() === "pm" && hours < 12)
            hour24 = hours + 12;
          else if (meridiem.toLowerCase() === "am" && hours === 12) hour24 = 0;
          slotTime = dayjs().hour(hour24).minute(minutes);
        } else {
          slotTime = dayjs(`2000-01-01 ${timeStr}`);
        }

        const offsetTime = slotTime.add(offsetMinutes, "minute");
        return {
          ...slot,
          timeStart: offsetTime.format(
            timeFormat === "12h" ? "h:mma" : "HH:mm"
          ),
          timeEnd: offsetTime
            .add(parseInt(eventDuration || 30), "minute")
            .format(timeFormat === "12h" ? "h:mma" : "HH:mm"),
        };
      });
    }

    // Apply minimum notice filter - filter out slots that are too soon
    if (minimumNoticeMinutes > 0) {
      const isToday = selectedDate.isSame(today, "day");

      console.log("=== Minimum Notice Filter ===");
      console.log("minimumNoticeMinutes:", minimumNoticeMinutes);
      console.log("isToday:", isToday);

      if (isToday) {
        const minimumBookingTime = now.add(minimumNoticeMinutes, "minute");
        console.log("Current time:", now.format("h:mma"));
        console.log(
          "Minimum booking time:",
          minimumBookingTime.format("h:mma")
        );

        const slotsBefore = timeSlots.length;
        timeSlots = timeSlots.filter((slot) => {
          const timeStr = slot.timeStart;
          let slotTime;

          if (timeStr.includes("am") || timeStr.includes("pm")) {
            const [timePart, meridiem] = timeStr.split(/(?=[ap]m)/i);
            const [hours, minutes] = timePart.split(":").map(Number);
            let hour24 = hours;
            if (meridiem.toLowerCase() === "pm" && hours < 12)
              hour24 = hours + 12;
            else if (meridiem.toLowerCase() === "am" && hours === 12)
              hour24 = 0;
            slotTime = selectedDate.hour(hour24).minute(minutes);
          } else {
            const [hours, minutes] = timeStr.split(":").map(Number);
            slotTime = selectedDate.hour(hours).minute(minutes);
          }

          return slotTime.isAfter(minimumBookingTime);
        });
        console.log(
          `Minimum notice filter: ${slotsBefore} slots -> ${timeSlots.length} slots`
        );
      }
    }

    // Apply limit first slot only - if enabled, only return the first available slot
    if (limits?.limitFirstSlotOnly && timeSlots.length > 0) {
      console.log("=== Limit First Slot Only ===");
      console.log("Returning only first slot:", timeSlots[0].timeStart);
      timeSlots = [timeSlots[0]];
    }

    // Filter out locked slots that aren't ours
    if (lockedSlots && lockedSlots.length > 0) {
      timeSlots = timeSlots.filter((slot) => {
        const slotTimeStart = slot.timeStart.toLowerCase();

        // Check if this slot is locked by someone else
        const matchingLock = lockedSlots.find((lock) => {
          return (
            lock.timeStart.toLowerCase() === slotTimeStart &&
            lock.lockId !== bookingLockId
          );
        });

        return !matchingLock;
      });
    }

    // Filter out already booked slots (accounting for multiple seats if enabled)
    if (existingBookings && existingBookings.length > 0) {
      const offerSeats = eventTypeData?.advanced?.offerSeats;
      const totalSeats = offerSeats?.enabled ? offerSeats.seats || 1 : 1;

      console.log("=== Filtering booked slots ===");
      console.log("existingBookings:", existingBookings);
      console.log("totalSeats:", totalSeats);

      // Helper to normalize time format for comparison (removes spaces, lowercase)
      const normalizeTime = (timeStr) => {
        if (!timeStr) return "";
        // Remove all spaces and convert to lowercase: "11:30 am" -> "11:30am"
        return timeStr.replace(/\s+/g, "").toLowerCase();
      };

      timeSlots = timeSlots.filter((slot) => {
        // Convert slot time to comparable format
        const slotTimeStart =
          timeFormat === "24h"
            ? normalizeTime(
                dayjs(`2000-01-01 ${slot.timeStart}`).format("h:mma")
              )
            : normalizeTime(slot.timeStart);

        // Count how many bookings exist for this time slot
        const bookingsForSlot = existingBookings.filter((booking) => {
          const bookingStart = normalizeTime(booking.timeStart);
          const isMatch = bookingStart === slotTimeStart;
          if (isMatch) {
            console.log(
              `Slot ${slotTimeStart} matches booking ${bookingStart}`
            );
          }
          return isMatch;
        }).length;

        // If offerSeats is enabled, only hide if all seats are booked
        // Otherwise, hide if there's any booking for this slot
        const shouldShow = bookingsForSlot < totalSeats;
        if (!shouldShow) {
          console.log(
            `Hiding slot ${slotTimeStart} - ${bookingsForSlot} bookings >= ${totalSeats} seats`
          );
        }
        return shouldShow;
      });
    }

    // Apply buffer times - filter out slots that would conflict with existing bookings + buffers
    // Buffer before: time needed before a booking (preparation time)
    // Buffer after: time needed after a booking (wrap-up time)
    // A slot conflicts with a booking if the slot's time range overlaps with the booking's buffered range
    if (
      (beforeBuffer > 0 || afterBuffer > 0) &&
      existingBookings &&
      existingBookings.length > 0
    ) {
      console.log("=== Applying buffer time filter ===");
      console.log(
        "beforeBuffer:",
        beforeBuffer,
        "minutes, afterBuffer:",
        afterBuffer,
        "minutes"
      );
      console.log("Existing bookings:", existingBookings);

      const slotDuration = parseInt(eventDuration || 30);

      timeSlots = timeSlots.filter((slot) => {
        const slotTimeStr = slot.timeStart;
        let slotStartTime;

        // Parse slot time
        if (slotTimeStr.includes("am") || slotTimeStr.includes("pm")) {
          const [timePart, meridiem] = slotTimeStr.split(/(?=[ap]m)/i);
          const [hours, minutes] = timePart.split(":").map(Number);
          let hour24 = hours;
          if (meridiem.toLowerCase() === "pm" && hours < 12)
            hour24 = hours + 12;
          else if (meridiem.toLowerCase() === "am" && hours === 12) hour24 = 0;
          slotStartTime = selectedDate.hour(hour24).minute(minutes).second(0);
        } else {
          const [hours, minutes] = slotTimeStr.split(":").map(Number);
          slotStartTime = selectedDate.hour(hours).minute(minutes).second(0);
        }

        const slotEndTime = slotStartTime.add(slotDuration, "minute");

        // Calculate the new slot's blocked range (including its own buffers)
        // This is the time that would be blocked if someone books this slot
        const newSlotBlockedStart = slotStartTime.subtract(
          beforeBuffer,
          "minute"
        );
        const newSlotBlockedEnd = slotEndTime.add(afterBuffer, "minute");

        // Check each existing booking for buffer conflicts
        for (const booking of existingBookings) {
          const bookingTimeStr = booking.timeStart;
          let bookingStartTime;

          // Parse booking time
          if (bookingTimeStr.includes("am") || bookingTimeStr.includes("pm")) {
            const [timePart, meridiem] = bookingTimeStr.split(/(?=[ap]m)/i);
            const [hours, minutes] = timePart.split(":").map(Number);
            let hour24 = hours;
            if (meridiem.toLowerCase() === "pm" && hours < 12)
              hour24 = hours + 12;
            else if (meridiem.toLowerCase() === "am" && hours === 12)
              hour24 = 0;
            bookingStartTime = selectedDate
              .hour(hour24)
              .minute(minutes)
              .second(0);
          } else {
            const [hours, minutes] = bookingTimeStr.split(":").map(Number);
            bookingStartTime = selectedDate
              .hour(hours)
              .minute(minutes)
              .second(0);
          }

          const bookingEndTime = bookingStartTime.add(slotDuration, "minute");

          // Calculate the "blocked range" for this booking including buffers
          // blockedStart = booking start - before buffer (prep time before booking)
          // blockedEnd = booking end + after buffer (wrap-up time after booking)
          const existingBlockedStart = bookingStartTime.subtract(
            beforeBuffer,
            "minute"
          );
          const existingBlockedEnd = bookingEndTime.add(afterBuffer, "minute");

          // A slot conflicts if its blocked range overlaps with the existing booking's blocked range
          // This ensures both the existing booking's buffers AND the new slot's buffers are respected
          // Overlap exists if: newSlotBlockedStart < existingBlockedEnd AND newSlotBlockedEnd > existingBlockedStart
          const hasConflict =
            newSlotBlockedStart.isBefore(existingBlockedEnd) &&
            newSlotBlockedEnd.isAfter(existingBlockedStart);

          if (hasConflict) {
            console.log(
              `Slot ${slotTimeStr} BLOCKED by booking ${bookingTimeStr} (existing blocked: ${existingBlockedStart.format(
                "h:mma"
              )} - ${existingBlockedEnd.format(
                "h:mma"
              )}, new slot blocked: ${newSlotBlockedStart.format(
                "h:mma"
              )} - ${newSlotBlockedEnd.format("h:mma")})`
            );
            return false;
          }
        }

        return true;
      });

      console.log("Slots after buffer filter:", timeSlots.length);
    }

    return timeSlots;
  }

  // Navigate to previous month (prevent going before current month)
  const prevMonth = () => {
    const previousMonth = selectedDate.subtract(1, "month");
    const currentMonth = dayjs().startOf("month");
    // Only allow going to previous month if it's not before the current month
    if (!previousMonth.startOf("month").isBefore(currentMonth)) {
      setSelectedDate(previousMonth);
    }
  };

  // Navigate to next month
  const nextMonth = () => {
    setSelectedDate(selectedDate.add(1, "month"));
  };

  // Check if we can go to previous month
  const canGoPrevMonth = () => {
    const previousMonth = selectedDate.subtract(1, "month");
    const currentMonth = dayjs().startOf("month");
    return !previousMonth.startOf("month").isBefore(currentMonth);
  };

  // Check if we can go to previous week
  const canGoPrevWeek = () => {
    const previousWeek = selectedDate.subtract(1, "week").startOf("week");
    const currentWeekStart = dayjs().startOf("week");
    return !previousWeek.isBefore(currentWeekStart);
  };

  // Navigate to previous week (prevent going before current week)
  const prevWeek = () => {
    if (canGoPrevWeek()) {
      setSelectedDate(selectedDate.subtract(1, "week"));
    }
  };

  // Navigate to next week
  const nextWeek = () => {
    setSelectedDate(selectedDate.add(1, "week"));
  };

  // Select a time slot with lock functionality
  const selectTime = async (time) => {
    // Validate duration is selected if multiple durations enabled
    if (
      eventTypeData?.allowMultipleDurations &&
      eventTypeData?.availableDurations?.length > 0 &&
      !selectedDuration
    ) {
      alert("Please select a duration before choosing a time slot.");
      return;
    }

    const lockSuccess = await lockTimeSlot(time);

    if (lockSuccess) {
      setSelectedTime(time);
      setOpenModal(true);
      startTimer();
    } else {
      alert(
        "This time slot is no longer available. Please select another time."
      );
      // Refresh available slots
      fetchExistingBookings(selectedDate);
      fetchLockedTimeSlots();
    }
  };
  const isFormValid = formData.name.trim() !== "" &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) &&
    formData.notes.trim() !== "";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({ ...prevState, [name]: value }));
  };

  // Handler for custom question inputs
  const handleCustomQuestionChange = (
    questionId,
    value,
    isMultiSelect = false
  ) => {
    setCustomQuestionAnswers((prev) => {
      if (isMultiSelect) {
        // For multi-select, toggle the value in an array
        const currentValues = prev[questionId] || [];
        if (currentValues.includes(value)) {
          return {
            ...prev,
            [questionId]: currentValues.filter((v) => v !== value),
          };
        } else {
          return { ...prev, [questionId]: [...currentValues, value] };
        }
      }
      return { ...prev, [questionId]: value };
    });
  };

  // Handler for recurring occurrences change
  const handleRecurringOccurrencesChange = (newOccurrences) => {
    const recurring = eventTypeData?.recurring;
    if (!recurring || !recurring.enabled) return;

    const maxOccurrences = recurring.maxOccurrences || 12;
    const validOccurrences = Math.min(
      Math.max(1, newOccurrences),
      maxOccurrences
    );
    setRecurringOccurrences(validOccurrences);

    // Calculate and display recurring dates (respects host availability)
    if (selectedDate && validOccurrences > 0) {
      const dates = calculateRecurringDates(
        selectedDate,
        recurring.unit,
        recurring.frequency,
        validOccurrences,
        availabilityData
      );
      setRecurringDates(dates);
      setShowRecurringPreview(validOccurrences > 1);
    }
  };

  // Render custom booking questions from advanced settings
  const renderCustomBookingQuestions = () => {
    const questions = eventTypeData?.advanced?.bookingQuestions;
    if (!questions || questions.length === 0) return null;

    // Filter out disabled questions
    const enabledQuestions = questions.filter((q) => q.enabled !== false);
    if (enabledQuestions.length === 0) return null;

    return enabledQuestions.map((question) => {
      const { id, type, label, placeholder, options, required, identifier } =
        question;
      const questionKey = id || identifier;

      switch (type) {
        case "Email":
          return (
            <div key={questionKey} style={{ marginBottom: "16px" }}>
              <label style={styles.formInputLabel} htmlFor={questionKey}>
                {label} {required && "*"}
              </label>
              <input
                type="email"
                id={questionKey}
                value={customQuestionAnswers[questionKey] || ""}
                onChange={(e) =>
                  handleCustomQuestionChange(questionKey, e.target.value)
                }
                style={styles.formInput}
                placeholder={placeholder || ""}
                required={required}
                disabled={isSubmitting}
              />
            </div>
          );

        case "Phone":
          return (
            <div key={questionKey} style={{ marginBottom: "16px" }}>
              <label style={styles.formInputLabel} htmlFor={questionKey}>
                {label} {required && "*"}
              </label>
              <input
                type="tel"
                id={questionKey}
                value={customQuestionAnswers[questionKey] || ""}
                onChange={(e) =>
                  handleCustomQuestionChange(questionKey, e.target.value)
                }
                style={styles.formInput}
                placeholder={placeholder || ""}
                required={required}
                disabled={isSubmitting}
              />
            </div>
          );

        case "Number":
          return (
            <div key={questionKey} style={{ marginBottom: "16px" }}>
              <label style={styles.formInputLabel} htmlFor={questionKey}>
                {label} {required && "*"}
              </label>
              <input
                type="number"
                id={questionKey}
                value={customQuestionAnswers[questionKey] || ""}
                onChange={(e) =>
                  handleCustomQuestionChange(questionKey, e.target.value)
                }
                style={styles.formInput}
                placeholder={placeholder || ""}
                required={required}
                disabled={isSubmitting}
              />
            </div>
          );

        case "ShortText":
          return (
            <div key={questionKey} style={{ marginBottom: "16px" }}>
              <label style={styles.formInputLabel} htmlFor={questionKey}>
                {label} {required && "*"}
              </label>
              <input
                type="text"
                id={questionKey}
                value={customQuestionAnswers[questionKey] || ""}
                onChange={(e) =>
                  handleCustomQuestionChange(questionKey, e.target.value)
                }
                style={styles.formInput}
                placeholder={placeholder || ""}
                required={required}
                disabled={isSubmitting}
              />
            </div>
          );

        case "LongText":
          return (
            <div key={questionKey} style={{ marginBottom: "16px" }}>
              <label style={styles.formInputLabel} htmlFor={questionKey}>
                {label} {required && "*"}
              </label>
              <textarea
                id={questionKey}
                value={customQuestionAnswers[questionKey] || ""}
                onChange={(e) =>
                  handleCustomQuestionChange(questionKey, e.target.value)
                }
                style={styles.formTextarea}
                placeholder={placeholder || ""}
                required={required}
                disabled={isSubmitting}
              />
            </div>
          );

        case "Address":
          return (
            <div key={questionKey} style={{ marginBottom: "16px" }}>
              <label style={styles.formInputLabel} htmlFor={questionKey}>
                {label} {required && "*"}
              </label>
              <textarea
                id={questionKey}
                value={customQuestionAnswers[questionKey] || ""}
                onChange={(e) =>
                  handleCustomQuestionChange(questionKey, e.target.value)
                }
                style={styles.formTextarea}
                placeholder={placeholder || "Enter your address"}
                required={required}
                disabled={isSubmitting}
              />
            </div>
          );

        case "Select":
          return (
            <div key={questionKey} style={{ marginBottom: "16px" }}>
              <label style={styles.formInputLabel} htmlFor={questionKey}>
                {label} {required && "*"}
              </label>
              <select
                id={questionKey}
                value={customQuestionAnswers[questionKey] || ""}
                onChange={(e) =>
                  handleCustomQuestionChange(questionKey, e.target.value)
                }
                style={styles.formInput}
                required={required}
                disabled={isSubmitting}
              >
                <option value="">{placeholder || "Select an option"}</option>
                {options?.map((option, idx) => (
                  <option key={idx} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          );

        case "MultiSelect":
        case "CheckboxGroup":
          return (
            <div key={questionKey} style={{ marginBottom: "16px" }}>
              <label style={styles.formInputLabel}>
                {label} {required && "*"}
              </label>
              <div style={{ marginTop: "8px" }}>
                {options?.map((option, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "8px",
                    }}
                  >
                    <input
                      type="checkbox"
                      id={`${questionKey}-${idx}`}
                      checked={(
                        customQuestionAnswers[questionKey] || []
                      ).includes(option)}
                      onChange={() =>
                        handleCustomQuestionChange(questionKey, option, true)
                      }
                      disabled={isSubmitting}
                      style={{ marginRight: "8px" }}
                    />
                    <label
                      htmlFor={`${questionKey}-${idx}`}
                      style={{ fontSize: "14px", color: "#374151" }}
                    >
                      {option}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          );

        case "RadioGroup":
          return (
            <div key={questionKey} style={{ marginBottom: "16px" }}>
              <label style={styles.formInputLabel}>
                {label} {required && "*"}
              </label>
              <div style={{ marginTop: "8px" }}>
                {options?.map((option, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "8px",
                    }}
                  >
                    <input
                      type="radio"
                      id={`${questionKey}-${idx}`}
                      name={questionKey}
                      value={option}
                      checked={customQuestionAnswers[questionKey] === option}
                      onChange={(e) =>
                        handleCustomQuestionChange(questionKey, e.target.value)
                      }
                      disabled={isSubmitting}
                      style={{ marginRight: "8px" }}
                    />
                    <label
                      htmlFor={`${questionKey}-${idx}`}
                      style={{ fontSize: "14px", color: "#374151" }}
                    >
                      {option}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          );

        case "Checkbox":
          return (
            <div
              key={questionKey}
              style={{
                marginBottom: "16px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <input
                type="checkbox"
                id={questionKey}
                checked={customQuestionAnswers[questionKey] || false}
                onChange={(e) =>
                  handleCustomQuestionChange(questionKey, e.target.checked)
                }
                disabled={isSubmitting}
                style={{ marginRight: "8px" }}
              />
              <label
                htmlFor={questionKey}
                style={{ fontSize: "14px", color: "#374151" }}
              >
                {label} {required && "*"}
              </label>
            </div>
          );

        case "MultipleEmails":
          return (
            <div key={questionKey} style={{ marginBottom: "16px" }}>
              <label style={styles.formInputLabel} htmlFor={questionKey}>
                {label} {required && "*"}
              </label>
              <textarea
                id={questionKey}
                value={customQuestionAnswers[questionKey] || ""}
                onChange={(e) =>
                  handleCustomQuestionChange(questionKey, e.target.value)
                }
                style={styles.formTextarea}
                placeholder={
                  placeholder || "Enter email addresses (one per line)"
                }
                required={required}
                disabled={isSubmitting}
              />
              <small style={{ color: "#6b7280", fontSize: "12px" }}>
                Enter one email per line
              </small>
            </div>
          );

        default:
          // Default to text input for unknown types
          return (
            <div key={questionKey} style={{ marginBottom: "16px" }}>
              <label style={styles.formInputLabel} htmlFor={questionKey}>
                {label} {required && "*"}
              </label>
              <input
                type="text"
                id={questionKey}
                value={customQuestionAnswers[questionKey] || ""}
                onChange={(e) =>
                  handleCustomQuestionChange(questionKey, e.target.value)
                }
                style={styles.formInput}
                placeholder={placeholder || ""}
                required={required}
                disabled={isSubmitting}
              />
            </div>
          );
      }
    });
  };

  const handleSubmitBooking = async () => {
    // Validate form data
    if (!formData.name || !formData.email || !formData.notes) {
      alert("Please fill in your name, email, and additional notes");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      alert("Please enter a valid email address");
      return;
    }

    // Validate required custom questions (only enabled ones)
    const bookingQuestions = eventTypeData?.advanced?.bookingQuestions || [];
    const enabledBookingQuestions = bookingQuestions.filter(
      (q) => q.enabled !== false
    );
    for (const question of enabledBookingQuestions) {
      if (question.required) {
        const questionKey = question.id || question.identifier;
        const answer = customQuestionAnswers[questionKey];

        // Check if answer is empty/missing
        if (
          answer === undefined ||
          answer === null ||
          answer === "" ||
          (Array.isArray(answer) && answer.length === 0) ||
          (typeof answer === "boolean" &&
            answer === false &&
            question.type === "Checkbox")
        ) {
          alert(`Please answer the required question: ${question.label}`);
          return;
        }
      }
    }

    // Check booking frequency limits if enabled
    const limitBookingFrequency = eventTypeData?.limits?.limitBookingFrequency;
    if (limitBookingFrequency?.enabled) {
      try {
        // Query for user's previous bookings by email for this event type
        const bookingsResponse = await axiosInstance.post("/graphql", {
          query: `
            query GetBookingsByEmail($eventTypeId: ID!, $email: String!) {
              getBookingsByEmail(eventTypeId: $eventTypeId, email: $email) {
                id
                date
                email
              }
            }
          `,
          variables: {
            eventTypeId: actualEventTypeId,
            email: formData.email.toLowerCase(),
          },
        });

        const userBookings =
          bookingsResponse.data?.data?.getBookingsByEmail ||
          bookingsResponse.data?.getBookingsByEmail ||
          [];

        const today = dayjs();

        // Check per day limit
        if (limitBookingFrequency.perDay?.enabled) {
          const limit = limitBookingFrequency.perDay.value || 1;
          const bookingsToday = userBookings.filter((b) =>
            dayjs(b.date).isSame(selectedDate, "day")
          ).length;
          if (bookingsToday >= limit) {
            alert(
              `You have reached your daily booking limit (${limit} booking${
                limit > 1 ? "s" : ""
              } per day)`
            );
            return;
          }
        }

        // Check per week limit
        if (limitBookingFrequency.perWeek?.enabled) {
          const limit = limitBookingFrequency.perWeek.value || 1;
          const weekStart = selectedDate.startOf("week");
          const weekEnd = selectedDate.endOf("week");
          const bookingsThisWeek = userBookings.filter((b) => {
            const bookingDate = dayjs(b.date);
            return (
              bookingDate.isAfter(weekStart) && bookingDate.isBefore(weekEnd)
            );
          }).length;
          if (bookingsThisWeek >= limit) {
            alert(
              `You have reached your weekly booking limit (${limit} booking${
                limit > 1 ? "s" : ""
              } per week)`
            );
            return;
          }
        }

        // Check per month limit
        if (limitBookingFrequency.perMonth?.enabled) {
          const limit = limitBookingFrequency.perMonth.value || 1;
          const monthStart = selectedDate.startOf("month");
          const monthEnd = selectedDate.endOf("month");
          const bookingsThisMonth = userBookings.filter((b) => {
            const bookingDate = dayjs(b.date);
            return (
              bookingDate.isAfter(monthStart) && bookingDate.isBefore(monthEnd)
            );
          }).length;
          if (bookingsThisMonth >= limit) {
            alert(
              `You have reached your monthly booking limit (${limit} booking${
                limit > 1 ? "s" : ""
              } per month)`
            );
            return;
          }
        }

        // Check per year limit
        if (limitBookingFrequency.perYear?.enabled) {
          const limit = limitBookingFrequency.perYear.value || 1;
          const yearStart = selectedDate.startOf("year");
          const yearEnd = selectedDate.endOf("year");
          const bookingsThisYear = userBookings.filter((b) => {
            const bookingDate = dayjs(b.date);
            return (
              bookingDate.isAfter(yearStart) && bookingDate.isBefore(yearEnd)
            );
          }).length;
          if (bookingsThisYear >= limit) {
            alert(
              `You have reached your yearly booking limit (${limit} booking${
                limit > 1 ? "s" : ""
              } per year)`
            );
            return;
          }
        }
      } catch (error) {
        console.warn("Could not check booking frequency limits:", error);
        // Continue with booking even if check fails
      }
    }

    // Set loading state to true
    setIsSubmitting(true);

    // Fix for parsing time and calculating end time correctly
    let endTime;
    // Use selectedDuration if multiple durations enabled, otherwise use default duration
    const duration =
      eventTypeData?.allowMultipleDurations && selectedDuration
        ? selectedDuration
        : eventTypeData?.duration || 30;

    // Parse the time from the 12h format (e.g., "11:00am")
    if (
      selectedTime.toLowerCase().includes("am") ||
      selectedTime.toLowerCase().includes("pm")
    ) {
      const timeStr = selectedTime.toLowerCase();
      const isPM = timeStr.includes("pm");

      // Extract hours and minutes
      let [hourMin, _] = timeStr.split(/am|pm/i);
      let [hours, minutes] = hourMin
        .split(":")
        .map((num) => parseInt(num.trim(), 10));

      // Adjust for PM if needed (except for 12 PM)
      if (isPM && hours !== 12) {
        hours += 12;
      }
      // Adjust for 12 AM
      if (!isPM && hours === 12) {
        hours = 0;
      }

      // Create a dayjs object and add duration
      const timeObj = dayjs().hour(hours).minute(minutes).second(0);
      const endTimeObj = timeObj.add(duration, "minute");

      // Format based on timeFormat setting
      endTime = endTimeObj.format(timeFormat === "12h" ? "h:mma" : "HH:mm");
    } else {
      // For 24h format (e.g., "14:00")
      const [hours, minutes] = selectedTime.split(":").map(Number);
      const timeObj = dayjs().hour(hours).minute(minutes).second(0);
      const endTimeObj = timeObj.add(duration, "minute");
      endTime = endTimeObj.format(timeFormat === "12h" ? "h:mma" : "HH:mm");
    }

    // Format custom question answers for the booking
    const formattedCustomAnswers =
      Object.keys(customQuestionAnswers).length > 0
        ? Object.entries(customQuestionAnswers).map(([questionId, answer]) => {
            const question = bookingQuestions.find(
              (q) => (q.id || q.identifier) === questionId
            );
            return {
              questionId,
              questionLabel: question?.label || questionId,
              questionType: question?.type || "ShortText",
              answer: Array.isArray(answer)
                ? answer.join(", ")
                : String(answer),
            };
          })
        : undefined;

    const bookingData = {
      eventTypeId: actualEventTypeId,
      date: selectedDate.format("YYYY-MM-DD"),
      timeStart: selectedTime,
      timeEnd: endTime,
      name: formData.name,
      email: formData.email,
      notes: formData.notes,
      lockId: bookingLockId,
      timezone:
        formData.timezone ||
        userTimezone ||
        Intl.DateTimeFormat().resolvedOptions().timezone, // Include booker's timezone
      // Include form submission ID from routing form if available
      ...(formData.formSubmissionId
        ? {
            formSubmissionId: formData.formSubmissionId,
            redirectSource: formData.redirectSource || "routing_form",
          }
        : {}),
      // Include custom question answers if any
      ...(formattedCustomAnswers
        ? { customAnswers: formattedCustomAnswers }
        : {}),
      // Include assigned team member if selected
      ...(selectedTeamMember
        ? { assignedHostId: selectedTeamMember.userId }
        : {}),
    };

    // Check if this is a recurring booking
    const isRecurringBooking =
      eventTypeData?.recurring?.enabled && recurringOccurrences > 1;

    if (isRecurringBooking) {
      // Use createRecurringBookings mutation for recurring bookings
      const recurringBookingData = {
        eventTypeId: actualEventTypeId,
        date: selectedDate.format("YYYY-MM-DD"),
        timeStart: selectedTime,
        timeEnd: endTime,
        name: formData.name,
        email: formData.email,
        notes: formData.notes,
        timezone:
          formData.timezone ||
          userTimezone ||
          Intl.DateTimeFormat().resolvedOptions().timeZone,
        occurrences: recurringOccurrences,
        ...(formattedCustomAnswers
          ? { customAnswers: formattedCustomAnswers }
          : {}),
      };

      axiosInstance
        .post("/graphql", {
          query: `
            mutation CreateRecurringBookings($input: CreateRecurringBookingsInput!) {
              createRecurringBookings(input: $input) {
                success
                message
                seriesId
                bookings {
                  id
                  date
                  timeStart
                  timeEnd
                }
              }
            }
          `,
          variables: {
            input: recurringBookingData,
          },
        })
        .then((response) => {
          setIsSubmitting(false);
          console.log("Recurring booking response:", response);

          const result =
            response.data?.data?.createRecurringBookings ||
            response.data?.createRecurringBookings;

          if (result?.success) {
            const bookingCount =
              result.bookings?.length || recurringOccurrences;
            setSuccessMessage(
              `Successfully booked ${bookingCount} recurring sessions for ${eventTypeData.title}! Check your email for confirmation details.`
            );

            // Reset form
            setFormData({
              name: "",
              email: "",
              notes: "",
              timezone: userTimezone,
            });
            setCustomQuestionAnswers({});
            setRecurringOccurrences(1);
            setRecurringDates([]);
            setShowRecurringPreview(false);

            // Clear time slot lock if we have a lock ID
            if (bookingLockId) {
              setBookingLockId(null);
              clearInterval(timerIntervalRef.current);
              setTimerActive(false);
            }

            // Refresh data
            fetchExistingBookings(selectedDate);
            fetchLockedTimeSlots();
          } else {
            console.error("Recurring booking failed:", result?.message);
            alert(
              result?.message ||
                "Failed to create recurring bookings. Please try again."
            );
          }
        })
        .catch((error) => {
          setIsSubmitting(false);
          console.error("Error creating recurring bookings:", error);
          alert("Failed to create recurring bookings. Please try again.");
          releaseTimeSlotLock();
        });

      return; // Exit early for recurring bookings
    }

    // Check if email verification is required
    // Note: requiresEmailVerification is a simple boolean, not an object with .enabled
    const requiresEmailVerification =
      eventTypeData?.advanced?.requiresEmailVerification === true;

    console.log(
      "Email verification check - requiresEmailVerification:",
      requiresEmailVerification
    );

    if (requiresEmailVerification) {
      // First initiate pending booking for email OTP verification
      axiosInstance
        .post("/graphql", {
          query: `
        mutation InitiatePendingBooking($input: BookingInput!) {
          initiatePendingBooking(input: $input) {
            success
            message
            pendingBookingId
            requiresVerification
          }
        }
      `,
          variables: {
            input: bookingData,
          },
        })
        .then((response) => {
          setIsSubmitting(false);
          console.log("Pending booking response:", response);

          const result =
            response.data?.data?.initiatePendingBooking ||
            response.data?.initiatePendingBooking;

          if (result?.success) {
            if (result.requiresVerification) {
              // Show OTP modal - store email before showing modal
              setPendingEmail(formData.email);
              setPendingBookingId(result.pendingBookingId);
              setOtpValue("");
              setOtpError("");
              setAttemptsRemaining(3);
              setShowOTPModal(true);
            } else {
              // Email verification not required - proceed with normal booking
              createBookingDirectly(bookingData);
              return;
            }
          } else {
            console.error("Pending booking failed:", result?.message);
            alert(result?.message || "Something went wrong. Please try again.");
          }
        })
        .catch((error) => {
          setIsSubmitting(false);
          console.error("Error initiating pending booking:", error);
          alert("Failed to initiate booking. Please try again.");
          releaseTimeSlotLock();
        });
    } else {
      // No email verification required - create booking directly
      createBookingDirectly(bookingData);
    }
  };

  // Verify OTP and complete booking
  const handleVerifyOTP = () => {
    if (!otpValue || otpValue.length !== 6) {
      setOtpError("Please enter a valid 6-digit code");
      return;
    }

    setOtpLoading(true);
    setOtpError("");

    axiosInstance
      .post("/graphql", {
        query: `
          mutation VerifyBookingOTP($pendingBookingId: ID!, $otp: String!) {
            verifyBookingOTP(pendingBookingId: $pendingBookingId, otp: $otp) {
              success
              message
              booking {
                id
                date
                timeStart
                timeEnd
              }
              attemptsRemaining
            }
          }
        `,
        variables: {
          pendingBookingId: pendingBookingId,
          otp: otpValue,
        },
      })
      .then((response) => {
        setOtpLoading(false);
        const result =
          response.data?.data?.verifyBookingOTP ||
          response.data?.verifyBookingOTP;

        if (result?.success) {
          // Success! Close modal and show success message
          setShowOTPModal(false);
          setSuccessMessage(
            `Your booking for ${eventTypeData.title} on ${selectedDate.format(
              "MMM D, YYYY"
            )} at ${selectedTime} has been confirmed!`
          );

          // Reset form
          setFormData({
            name: "",
            email: "",
            notes: "",
            timezone: userTimezone,
          });

          // Clear time slot lock
          if (bookingLockId) {
            setBookingLockId(null);
            clearInterval(timerIntervalRef.current);
            setTimerActive(false);
          }

          // Refresh data
          fetchExistingBookings(selectedDate);
          fetchLockedTimeSlots();

          // Handle redirect on booking if enabled
          if (
            eventTypeData?.advanced?.redirectOnBooking?.enabled &&
            eventTypeData?.advanced?.redirectOnBooking?.url
          ) {
            const redirectUrl = eventTypeData.advanced.redirectOnBooking.url;
            setTimeout(() => {
              const fullUrl = redirectUrl.startsWith("http")
                ? redirectUrl
                : `https://${redirectUrl}`;
              window.location.href = fullUrl;
            }, 2000);
          }
        } else {
          // Show error
          const otpMsg = result?.message || "Invalid verification code";
          if (otpMsg.includes("MEETING_QUOTA_EXCEEDED")) {
            setShowOTPModal(false);
            alert("Unable to schedule this meeting. The scheduler's meeting time limit has been reached. Please contact the scheduler to resolve this issue.");
          } else {
            setOtpError(otpMsg);
          }
          if (result?.attemptsRemaining !== undefined) {
            setAttemptsRemaining(result.attemptsRemaining);
          }

          // If no attempts remaining, close modal
          if (result?.attemptsRemaining === 0) {
            setTimeout(() => {
              setShowOTPModal(false);
              alert("Too many incorrect attempts. Please try booking again.");
            }, 1500);
          }
        }
      })
      .catch((error) => {
        setOtpLoading(false);
        console.error("Error verifying OTP:", error);
        setOtpError("Failed to verify code. Please try again.");
      });
  };

  // Resend OTP
  const handleResendOTP = () => {
    setOtpLoading(true);
    setOtpError("");

    axiosInstance
      .post("/graphql", {
        query: `
          mutation ResendBookingOTP($pendingBookingId: ID!) {
            resendBookingOTP(pendingBookingId: $pendingBookingId) {
              success
              message
            }
          }
        `,
        variables: {
          pendingBookingId: pendingBookingId,
        },
      })
      .then((response) => {
        setOtpLoading(false);
        const result =
          response.data?.data?.resendBookingOTP ||
          response.data?.resendBookingOTP;

        if (result?.success) {
          setOtpValue("");
          setAttemptsRemaining(3);
          setOtpError("New verification code sent to your email.");
        } else {
          setOtpError(result?.message || "Failed to resend code.");
        }
      })
      .catch((error) => {
        setOtpLoading(false);
        console.error("Error resending OTP:", error);
        setOtpError("Failed to resend code. Please try again.");
      });
  };

  // Helper function to create booking directly (when email verification is not required)
  const createBookingDirectly = (bookingData) => {
    axiosInstance
      .post("/graphql", {
        query: `
      mutation CreateBooking($input: BookingInput!) {
        createBooking(input: $input) {
          id
          date
          timeStart
          timeEnd
          name
          email
          notes
          eventTypeId
          timezone
        }
      }
    `,
        variables: {
          input: bookingData,
        },
      })
      .then((response) => {
        setIsSubmitting(false);
        console.log("Booking response:", response);

        // Properly handle the GraphQL response
        if (
          response.data?.data?.createBooking ||
          response.data?.createBooking
        ) {
          const resultBookingData =
            response.data?.data?.createBooking || response.data?.createBooking;

          // Set success message
          setSuccessMessage(
            `Your booking for ${eventTypeData.title} on ${selectedDate.format(
              "MMM D, YYYY"
            )} at ${selectedTime} has been confirmed!`
          );

          // Reset form
          setFormData({
            name: "",
            email: "",
            notes: "",
            timezone: userTimezone,
          });

          // Clear time slot lock if we have a lock ID
          if (bookingLockId) {
            setBookingLockId(null);
            clearInterval(timerIntervalRef.current);
            setTimerActive(false);
          }

          // Refresh data
          fetchExistingBookings(selectedDate);
          fetchLockedTimeSlots();

          // Handle redirect on booking if enabled
          if (
            eventTypeData?.advanced?.redirectOnBooking?.enabled &&
            eventTypeData?.advanced?.redirectOnBooking?.url
          ) {
            const redirectUrl = eventTypeData.advanced.redirectOnBooking.url;
            console.log("Redirecting to:", redirectUrl);
            // Delay redirect slightly to show success message
            setTimeout(() => {
              // Ensure URL has protocol
              const fullUrl = redirectUrl.startsWith("http")
                ? redirectUrl
                : `https://${redirectUrl}`;
              window.location.href = fullUrl;
            }, 2000);
          }
        } else {
          // Handle error
          console.error("Unexpected response format", response);
          setSuccessMessage(""); // Clear any previous success message
          alert("Something went wrong. Please try again.");
        }
      })
      .catch((error) => {
        setIsSubmitting(false);
        console.error("Error booking slot", error);
        const errorMsg = error?.message || error?.graphQLErrors?.[0]?.message || "";
        if (errorMsg.includes("MEETING_QUOTA_EXCEEDED")) {
          alert("Unable to schedule this meeting. The scheduler's meeting time limit has been reached. Please contact the scheduler to resolve this issue.");
        } else {
          alert("Failed to save your booking. Please try again.");
        }
        releaseTimeSlotLock();
      });
  };

  // Parse eventTypeId and formData from URL on component mount
  useEffect(() => {
    try {
      // Check if this is the new slug-based URL structure
      if (params.username && params.slug) {
        console.log(
          "Using slug-based URL - Username:",
          params.username,
          "Slug:",
          params.slug
        );
        setEventTypeId({
          type: "slug",
          username: params.username,
          slug: params.slug,
        });
      } else {
        // Fall back to the old eventTypeId parameter
        const urlParams = new URLSearchParams(location.search);
        const id = urlParams.get("eventTypeId");

        if (!id) {
          setError(
            "No event type ID provided. Please use a valid event type link."
          );
          setLoading(false);
          return;
        }

        console.log("Event type ID from URL:", id);
        setEventTypeId({ type: "id", value: id });
      }

      const urlParams = new URLSearchParams(location.search);
      const formDataParam = urlParams.get("formData");

      // Parse form data if it exists from routing form submission
      if (formDataParam) {
        try {
          const parsedFormData = JSON.parse(decodeURIComponent(formDataParam));
          console.log("Form data from routing form:", parsedFormData);

          // If we have a formSubmissionId, store it for the booking
          if (parsedFormData.formSubmissionId) {
            setFormData((prevFormData) => ({
              ...prevFormData,
              formSubmissionId: parsedFormData.formSubmissionId,
              redirectSource: parsedFormData.redirectSource || "routing_form",
            }));
          }

          // If we have pre-filled form data fields, apply them
          if (parsedFormData.fields) {
            const fieldsData = parsedFormData.fields;

            // Apply any pre-filled data from the routing form
            setFormData((prevFormData) => ({
              ...prevFormData,
              name:
                fieldsData.name || fieldsData.firstName
                  ? `${fieldsData.firstName || ""} ${
                      fieldsData.lastName || ""
                    }`.trim()
                  : prevFormData.name,
              email: fieldsData.email || prevFormData.email,
              notes:
                fieldsData.notes || fieldsData.message || prevFormData.notes,
            }));
          }
        } catch (parseError) {
          console.error("Error parsing form data from URL:", parseError);
          // Continue without the form data
        }
      }
    } catch (error) {
      console.error("Error parsing URL parameters:", error);
      setError("Invalid URL parameters. Please use a valid event type link.");
      setLoading(false);
    }
  }, [params.username, params.slug, location.search]);

  // Fetch event type and availability data when eventTypeId is available
  useEffect(() => {
    if (!eventTypeId) return;

    const fetchData = async () => {
      try {
        console.log("Fetching event type with:", eventTypeId);

        let response;

        // Use different GraphQL queries based on the eventTypeId type
        if (eventTypeId.type === "slug") {
          // Use the new slug-based query
          response = await axiosInstance.post("/graphql", {
            query: `
            query GetEventTypeBySlug($username: String!, $slug: String!) {
              getEventTypeBySlug(username: $username, slug: $slug) {
                id
                title
                url
                description
                duration
                allowMultipleDurations
                availableDurations
                assignment
                timezone
                availabilityId
                teamId
                owner {
                  id
                  name
                  companyName
                }
                limits {
                  beforeEvent
                  afterEvent
                  minimumNotice {
                    value
                    unit
                  }
                  timeSlotIntervals
                  limitBookingFrequency {
                    enabled
                    perDay { enabled, value }
                    perWeek { enabled, value }
                    perMonth { enabled, value }
                    perYear { enabled, value }
                  }
                  limitFirstSlotOnly
                  limitTotalBookingDuration {
                    enabled
                    perDay { enabled, value }
                    perWeek { enabled, value }
                    perMonth { enabled, value }
                    perYear { enabled, value }
                  }
                  limitFutureBookings {
                    enabled
                    limitByDays
                    days { value, unit }
                    dateRange { fromDate, toDate }
                  }
                  offsetStartTimes {
                    enabled
                    value
                  }
                }
                advanced {
                  eventNameInCalendar
                  layout {
                    month
                    weekly
                    column
                    defaultView
                  }
                  bookingQuestions {
                    id
                    title
                    type
                    identifier
                    label
                    placeholder
                    options
                    required
                    enabled
                  }
                  requiresConfirmation {
                    enabled
                    type
                    noticeTime {
                      value
                      unit
                    }
                  }
                  requiresEmailVerification
                  hideNotesInCalendar
                  redirectOnBooking {
                    enabled
                    url
                  }
                  enablePrivateUrl {
                    enabled
                    url
                  }
                  offerSeats {
                    enabled
                    seats
                    shareAttendeeInfo
                    showAvailableSeats
                  }
                  lockTimezone
                }
                recurring {
                  enabled
                  frequency
                  unit
                  maxOccurrences
                }
              }
            }
          `,
            variables: {
              username: eventTypeId.username,
              slug: eventTypeId.slug,
            },
          });
        } else {
          // Use the old ID-based query for backward compatibility
          response = await axiosInstance.post("/graphql", {
            query: `
            query GetEventTypePublic($id: ID!) {
              getEventTypePublic(id: $id) {
                id
                title
                url
                description
                duration
                allowMultipleDurations
                availableDurations
                assignment
                timezone
                availabilityId
                owner {
                  id
                  name
                  companyName
                }
                limits {
                  beforeEvent
                  afterEvent
                  minimumNotice {
                    value
                    unit
                  }
                  timeSlotIntervals
                  limitBookingFrequency {
                    enabled
                    perDay { enabled, value }
                    perWeek { enabled, value }
                    perMonth { enabled, value }
                    perYear { enabled, value }
                  }
                  limitFirstSlotOnly
                  limitTotalBookingDuration {
                    enabled
                    perDay { enabled, value }
                    perWeek { enabled, value }
                    perMonth { enabled, value }
                    perYear { enabled, value }
                  }
                  limitFutureBookings {
                    enabled
                    limitByDays
                    days { value, unit }
                    dateRange { fromDate, toDate }
                  }
                  offsetStartTimes {
                    enabled
                    value
                  }
                }
                advanced {
                  eventNameInCalendar
                  layout {
                    month
                    weekly
                    column
                    defaultView
                  }
                  bookingQuestions {
                    id
                    title
                    type
                    identifier
                    label
                    placeholder
                    options
                    required
                    enabled
                  }
                  requiresConfirmation {
                    enabled
                    type
                    noticeTime {
                      value
                      unit
                    }
                  }
                  requiresEmailVerification
                  hideNotesInCalendar
                  redirectOnBooking {
                    enabled
                    url
                  }
                  enablePrivateUrl {
                    enabled
                    url
                  }
                  offerSeats {
                    enabled
                    seats
                    shareAttendeeInfo
                    showAvailableSeats
                  }
                  lockTimezone
                }
                recurring {
                  enabled
                  frequency
                  unit
                  maxOccurrences
                }
              }
            }
          `,
            variables: {
              id: eventTypeId.value,
            },
          });
        }

        console.log("Full GraphQL response:", response);
        console.log("Response data structure:", JSON.stringify(response.data));

        // Check all possible locations of the event data
        let eventData = null;

        // For slug-based queries
        if (eventTypeId.type === "slug") {
          if (response.data?.data?.getEventTypeBySlug) {
            eventData = response.data.data.getEventTypeBySlug;
            console.log(
              "Found slug-based data in response.data.data.getEventTypeBySlug"
            );
          } else if (response.data?.getEventTypeBySlug) {
            eventData = response.data.getEventTypeBySlug;
            console.log(
              "Found slug-based data in response.data.getEventTypeBySlug"
            );
          }
        } else {
          // For ID-based queries (backward compatibility)
          if (response.data?.data?.getEventTypePublic) {
            eventData = response.data.data.getEventTypePublic;
            console.log(
              "Found ID-based data in response.data.data.getEventTypePublic"
            );
          } else if (response.data?.getEventTypePublic) {
            eventData = response.data.getEventTypePublic;
            console.log(
              "Found ID-based data in response.data.getEventTypePublic"
            );
          }
        }

        if (eventData) {
          console.log("Event data found:", eventData);

          console.log("Event timezone from GraphQL:", eventData.timezone);
          console.log("Event limits from GraphQL:", eventData.limits);
          console.log("Event advanced from GraphQL:", eventData.advanced);

          // Set event data
          setEventTypeData({
            id: eventData.id,
            title: eventData.title,
            owner: eventData.owner?.name || "Event Owner",
            ownerAvatar: null,
            url: eventData.url,
            description: eventData.description,
            duration: eventData.duration,
            allowMultipleDurations: eventData.allowMultipleDurations,
            availableDurations: eventData.availableDurations,
            assignment: eventData.assignment,
            timezone: eventData.timezone,
            availabilityId: eventData.availabilityId,
            teamId: eventData.teamId,
            limits: eventData.limits,
            advanced: eventData.advanced,
            recurring: eventData.recurring,
          });

          // Fetch team members if this is a team event type
          if (eventData.teamId) {
            console.log(
              "Team event detected, fetching team members for team:",
              eventData.teamId
            );
            setTeamMembersLoading(true);
            try {
              const teamMembersResponse = await axiosInstance.post("/graphql", {
                query: `
                query GetTeamMembersPublic($teamId: ID!) {
                  getTeamMembersPublic(teamId: $teamId) {
                    id
                    userId
                    name
                    email
                    avatar
                    role
                    availabilityId
                  }
                }
                `,
                variables: { teamId: eventData.teamId },
              });

              const members =
                teamMembersResponse.data?.data?.getTeamMembersPublic ||
                teamMembersResponse.data?.getTeamMembersPublic ||
                [];

              // Check if owner is already in the list (by userId or name)
              const ownerInList = members.some(
                (m) =>
                  m.userId === eventData.owner?.id ||
                  m.name === eventData.owner?.name
              );

              // If owner not in list and we have valid owner info, add them at the beginning
              if (
                !ownerInList &&
                eventData.owner?.id &&
                eventData.owner?.name
              ) {
                members.unshift({
                  id: `owner-${eventData.owner.id}`,
                  userId: eventData.owner.id,
                  name: eventData.owner.name,
                  email: null,
                  avatar: null,
                  role: "owner",
                });
              }

              console.log("Team members fetched (including owner):", members);
              setTeamMembers(members);
              setIsTeamEvent(members.length > 0);

              // Fetch availabilities for all team members
              if (members.length > 0) {
                const availabilityPromises = members
                  .filter((m) => m.availabilityId)
                  .map(async (member) => {
                    try {
                      const resp = await axiosInstance.post("/graphql", {
                        query: `
                          query GetAvailabilityPublic($id: ID!) {
                            getAvailabilityPublic(id: $id) {
                              id
                              name
                              timezone
                              days
                              timeSlots {
                                timeStart
                                timeEnd
                              }
                              dayTimeSlots {
                                day
                                slots {
                                  timeStart
                                  timeEnd
                                }
                              }
                              overrides {
                                date
                                timeSlots {
                                  timeStart
                                  timeEnd
                                }
                                isUnavailable
                              }
                            }
                          }
                        `,
                        variables: { id: member.availabilityId },
                      });
                      const data =
                        resp.data?.data?.getAvailabilityPublic ||
                        resp.data?.getAvailabilityPublic;
                      return { memberId: member.userId, availability: data };
                    } catch (e) {
                      console.error(
                        `Failed to fetch availability for ${member.name}:`,
                        e
                      );
                      return null;
                    }
                  });

                const results = await Promise.all(availabilityPromises);
                const avMap = {};
                results.filter(Boolean).forEach((r) => {
                  avMap[r.memberId] = r.availability;
                });
                setTeamMembersAvailabilities(avMap);
                console.log("Team members availabilities fetched:", avMap);
                // Combined availability will be calculated in useEffect after originalAvailability is also ready
              }
            } catch (teamError) {
              console.error("Error fetching team members:", teamError);
            } finally {
              setTeamMembersLoading(false);
            }
          }

          // Set default selected duration if multiple durations enabled
          if (
            eventData.allowMultipleDurations &&
            eventData.availableDurations?.length > 0
          ) {
            setSelectedDuration(eventData.availableDurations[0]);
          }

          // Set default view mode based on advanced layout settings
          if (eventData.advanced?.layout?.defaultView) {
            const defaultViewMap = {
              Month: "month",
              Weekly: "week",
              Column: "day",
            };
            const mappedView =
              defaultViewMap[eventData.advanced.layout.defaultView];
            if (mappedView) {
              setViewMode(mappedView);
            }
          }

          // Store the actual eventTypeId for other functions that need it
          setActualEventTypeId(eventData.id);

          // Now fetch availability data using the availabilityId from event type
          if (eventData.availabilityId) {
            try {
              const availResponse = await axiosInstance.post("/graphql", {
                query: `
                query GetAvailabilityPublic($id: ID!) {
                  getAvailabilityPublic(id: $id) {
                    id
                    name
                    timezone
                    days
                    timeSlots {
                      timeStart
                      timeEnd
                    }
                    dayTimeSlots {
                      day
                      slots {
                        timeStart
                        timeEnd
                      }
                    }
                    overrides {
                      date
                      timeSlots {
                        timeStart
                        timeEnd
                      }
                      isUnavailable
                    }
                  }
                }
              `,
                variables: {
                  id: eventData.availabilityId,
                },
              });

              console.log("Availability response:", availResponse);

              // Similar approach to handle different response structures
              let availabilityData = null;

              if (availResponse.data?.data?.getAvailabilityPublic) {
                availabilityData =
                  availResponse.data.data.getAvailabilityPublic;
              } else if (availResponse.data?.getAvailabilityPublic) {
                availabilityData = availResponse.data.getAvailabilityPublic;
              }

              if (availabilityData) {
                console.log("Setting availability data:", availabilityData);
                setAvailabilityData(availabilityData);
                setOriginalAvailability(availabilityData); // Store original for fallback
              } else {
                console.warn("No availability data found, using default");
                // Fallback to default availability
                const defaultAvailability = {
                  name: "Default Availability",
                  timezone: "UTC",
                  days: [
                    "Monday",
                    "Tuesday",
                    "Wednesday",
                    "Thursday",
                    "Friday",
                  ],
                  timeSlots: [{ timeStart: "09:00", timeEnd: "17:00" }],
                };
                setAvailabilityData(defaultAvailability);
                setOriginalAvailability(defaultAvailability);
              }
            } catch (error) {
              console.error("Error fetching availability:", error);
              // Fallback to default availability
              const defaultAvailability = {
                name: "Default Availability",
                timezone: "UTC",
                days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                timeSlots: [{ timeStart: "09:00", timeEnd: "17:00" }],
              };
              setAvailabilityData(defaultAvailability);
              setOriginalAvailability(defaultAvailability);
            }
          } else {
            console.warn("No availabilityId provided, using default");
            // Fallback to default availability if no availabilityId is provided
            const defaultAvailability = {
              name: "Default Availability",
              timezone: "UTC",
              days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              timeSlots: [{ timeStart: "09:00", timeEnd: "17:00" }],
            };
            setAvailabilityData(defaultAvailability);
            setOriginalAvailability(defaultAvailability);
          }
        } else {
          console.error("No event data found in response:", response.data);
          setError(
            "Event type not found. Please check the event ID and try again."
          );
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError(
          "An error occurred while loading event data. Please try again later."
        );
        setLoading(false);
      }
    };

    fetchData();
  }, [eventTypeId]);

  useEffect(() => {
    if (availabilityData && selectedDate) {
      // Fetch existing bookings for the selected date
      fetchExistingBookings(selectedDate);
      fetchLockedTimeSlots();

      // Determine which timezone to use based on lockTimezone setting
      let timezoneToUse;
      if (eventTypeData?.advanced?.lockTimezone) {
        // Use event owner's timezone when lockTimezone is enabled
        timezoneToUse =
          eventTypeData?.timezone || availabilityData?.timezone || "UTC";
        console.log(
          "lockTimezone enabled - using event timezone:",
          timezoneToUse
        );
      } else {
        // Detect the user's timezone
        timezoneToUse = Intl.DateTimeFormat().resolvedOptions().timeZone;
        console.log("Using user's timezone:", timezoneToUse);
      }
      setUserTimezone(timezoneToUse);
    }
  }, [
    selectedDate,
    actualEventTypeId,
    eventTypeData?.advanced?.lockTimezone,
    eventTypeData?.timezone,
    availabilityData?.timezone,
  ]);

  useEffect(() => {
    if (userTimezone) {
      setFormData((prevState) => ({ ...prevState, timezone: userTimezone }));
    }
  }, [userTimezone]);

  useEffect(() => {
    if (availabilityData) {
      const slots = getAvailableSlots(
        availabilityData,
        selectedDate,
        eventTypeData?.duration
      );
      setAvailableSlots(slots);
    }
  }, [
    selectedDate,
    availabilityData,
    eventTypeData,
    timeFormat,
    existingBookings,
    lockedSlots,
  ]);

  // Update availability when team member selection changes
  useEffect(() => {
    if (!isTeamEvent) return;

    if (selectedTeamMember === null) {
      // "Any Available" - use combined availability from all team members
      if (combinedAvailability) {
        console.log("Switching to combined availability for 'Any Available'");
        setAvailabilityData(combinedAvailability);
      } else if (originalAvailability) {
        // Fallback to original event type availability
        console.log("No combined availability, using original");
        setAvailabilityData(originalAvailability);
      }
    } else {
      // Specific member selected - use their availability
      const memberAvailability =
        teamMembersAvailabilities[selectedTeamMember.userId];
      if (memberAvailability) {
        console.log(
          `Switching to ${selectedTeamMember.name}'s availability:`,
          memberAvailability
        );
        setAvailabilityData(memberAvailability);
      } else if (originalAvailability) {
        // Member doesn't have availability, fallback to original
        console.log(
          `${selectedTeamMember.name} has no availability, using original`
        );
        setAvailabilityData(originalAvailability);
      }
    }
  }, [
    selectedTeamMember,
    isTeamEvent,
    teamMembersAvailabilities,
    combinedAvailability,
    originalAvailability,
  ]);

  // Calculate combined availability when team members' availabilities and original availability are both ready
  useEffect(() => {
    if (!isTeamEvent) return;

    // Get all team members' availabilities
    const memberAvailabilities = Object.values(
      teamMembersAvailabilities
    ).filter(Boolean);

    // Include original availability (event owner's) in the combination
    const allAvailabilities = [...memberAvailabilities];
    if (originalAvailability) {
      // Check if originalAvailability is not already in the list (by id)
      const alreadyIncluded = memberAvailabilities.some(
        (av) => av.id === originalAvailability.id
      );
      if (!alreadyIncluded) {
        allAvailabilities.push(originalAvailability);
      }
    }

    if (allAvailabilities.length > 0) {
      const combined = combineAvailabilities(allAvailabilities);
      setCombinedAvailability(combined);
      console.log(
        "Combined availability calculated (including owner):",
        combined
      );

      // If "Any Available" is currently selected, update the display
      if (selectedTeamMember === null) {
        setAvailabilityData(combined);
      }
    }
  }, [isTeamEvent, teamMembersAvailabilities, originalAvailability]);

  const renderMonthView = () => {
    const daysInMonth = selectedDate.daysInMonth();
    const firstDay = selectedDate.startOf("month").day(); // 0 = Sunday, 1 = Monday, etc.
    const days = [];
    const weekdays = ["S", "M", "T", "W", "T", "F", "S"];

    // Create days for current month
    const today = dayjs().startOf("day");
    for (let i = 1; i <= daysInMonth; i++) {
      const date = selectedDate.date(i);
      const isToday =
        date.format("YYYY-MM-DD") === dayjs().format("YYYY-MM-DD");
      const isSelected =
        date.format("YYYY-MM-DD") === selectedDate.format("YYYY-MM-DD");
      const dayAvailable = availabilityData?.days?.includes(
        date.format("dddd")
      );
      // Check if date is in the past (before today)
      const isPast = date.startOf("day").isBefore(today);

      days.push({
        date: i,
        isCurrentMonth: true,
        isToday,
        isSelected,
        isAvailable: dayAvailable && !isPast, // Past dates are not available
        isPast,
        fullDate: date,
      });
    }

    // Add empty cells for days before the first day of the month
    const emptyCellsBefore = [];
    for (let i = 0; i < firstDay; i++) {
      emptyCellsBefore.push({
        date: null,
        isCurrentMonth: false,
      });
    }

    // Combine the empty cells and the days of the month
    const allDays = [...emptyCellsBefore, ...days];

    return (
      <div>
        <div style={styles.monthHeader}>
          <button
            onClick={prevMonth}
            style={{
              ...styles.navButton,
              ...(canGoPrevMonth()
                ? {}
                : { opacity: 0.3, cursor: "not-allowed" }),
            }}
            disabled={!canGoPrevMonth()}
          >
            <ChevronLeft size={16} />
          </button>
          <h2 style={styles.monthTitle}>{selectedDate.format("MMMM YYYY")}</h2>
          <button onClick={nextMonth} style={styles.navButton}>
            <ChevronRight size={16} />
          </button>
        </div>

        <div style={styles.weekdaysGrid}>
          {weekdays.map((day, index) => (
            <div key={index} style={styles.weekday}>
              {day}
            </div>
          ))}
        </div>

        <div style={styles.daysGrid}>
          {allDays.map((day, index) => (
            <div key={index} style={styles.dayCell}>
              {day.date && (
                <button
                  style={{
                    ...styles.dayButton,
                    ...(day.isSelected ? styles.dayButtonSelected : {}),
                    ...(day.isToday && !day.isSelected
                      ? styles.dayButtonToday
                      : {}),
                    ...(!day.isAvailable ? styles.dayButtonUnavailable : {}),
                  }}
                  onClick={() =>
                    day.isAvailable && setSelectedDate(day.fullDate)
                  }
                  disabled={!day.isAvailable}
                >
                  {day.date}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    // Get start and end of the current week
    const startOfWeek = selectedDate.startOf("week");
    const weekdays = [];
    const today = dayjs().startOf("day");

    // Generate weekdays
    for (let i = 0; i < 7; i++) {
      const date = startOfWeek.add(i, "day");
      const isToday =
        date.format("YYYY-MM-DD") === dayjs().format("YYYY-MM-DD");
      const isSelected =
        date.format("YYYY-MM-DD") === selectedDate.format("YYYY-MM-DD");
      const dayAvailable = availabilityData?.days?.includes(
        date.format("dddd")
      );
      // Check if date is in the past (before today)
      const isPast = date.startOf("day").isBefore(today);

      weekdays.push({
        date,
        isToday,
        isSelected,
        isAvailable: dayAvailable && !isPast, // Past dates are not available
        isPast,
      });
    }

    return (
      <div>
        <div style={styles.monthHeader}>
          <button
            onClick={prevWeek}
            style={{
              ...styles.navButton,
              ...(canGoPrevWeek()
                ? {}
                : { opacity: 0.3, cursor: "not-allowed" }),
            }}
            disabled={!canGoPrevWeek()}
          >
            <ChevronLeft size={16} />
          </button>
          <h2 style={styles.monthTitle}>
            {startOfWeek.format("MMM D")} -{" "}
            {startOfWeek.add(6, "day").format("MMM D, YYYY")}
          </h2>
          <button onClick={nextWeek} style={styles.navButton}>
            <ChevronRight size={16} />
          </button>
        </div>

        <div style={{ display: "flex", gap: "6px", marginBottom: "12px" }}>
          {weekdays.map((day, index) => (
            <div key={index} style={{ flex: 1, textAlign: "center" }}>
              <div
                style={{
                  fontSize: "12px",
                  color: "#6b7280",
                  marginBottom: "4px",
                }}
              >
                {day.date.format("ddd")}
              </div>
              <button
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "50%",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "14px",
                  border:
                    day.isToday && !day.isSelected
                      ? "1px solid #3b82f6"
                      : "none",
                  background: day.isSelected ? "#3b82f6" : "transparent",
                  color: day.isSelected
                    ? "#ffffff"
                    : day.isAvailable
                    ? "#111827"
                    : "#d1d5db",
                  cursor: day.isAvailable ? "pointer" : "not-allowed",
                  fontWeight: day.isToday ? "bold" : "normal",
                }}
                onClick={() => day.isAvailable && setSelectedDate(day.date)}
                disabled={!day.isAvailable}
              >
                {day.date.format("D")}
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderDayView = () => {
    return (
      <div>
        <div style={styles.monthHeader}>
          <button
            onClick={() => setSelectedDate(selectedDate.subtract(1, "day"))}
            style={styles.navButton}
          >
            <ChevronLeft size={16} />
          </button>
          <h2 style={styles.monthTitle}>
            {selectedDate.format("dddd, MMMM D, YYYY")}
          </h2>
          <button
            onClick={() => setSelectedDate(selectedDate.add(1, "day"))}
            style={styles.navButton}
          >
            <ChevronRight size={16} />
          </button>
        </div>

        <div
          style={{
            padding: "12px",
            backgroundColor: "#f9fafb",
            borderRadius: "8px",
            textAlign: "center",
          }}
        >
          <p style={{ color: "#6b7280", fontSize: "14px" }}>
            {availabilityData?.days?.includes(selectedDate.format("dddd"))
              ? `Available ${availabilityData?.timeSlots?.[0]?.timeStart} - ${availabilityData?.timeSlots?.[0]?.timeEnd}`
              : "Not available today"}
          </p>
        </div>
      </div>
    );
  };

  // Render error state
  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <h3 style={styles.errorTitle}>Error</h3>
          <p style={styles.errorMessage}>{error}</p>
          <button
            style={styles.errorButton}
            onClick={() => (window.location.href = "/")}
          >
            Return to Homepage
          </button>
        </div>
      </div>
    );
  }

  // Render loading state
  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.flexRow}>
          {/* Left sidebar with event details */}
          <div style={styles.sidebar}>
            {/* User profile section */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                marginBottom: "24px",
              }}
            >
              {/* Avatar and name in one row */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "20px",
                }}
              >
                <div
                  style={{
                    width: "36px",
                    height: "36px",
                    backgroundColor: "#3b82f6",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#ffffff",
                    fontSize: "14px",
                    overflow: "hidden",
                  }}
                >
                  {eventTypeData?.ownerAvatar ? (
                    <img
                      src={eventTypeData.ownerAvatar}
                      alt={eventTypeData?.owner || "User"}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    eventTypeData?.owner?.charAt(0)?.toUpperCase() || "P"
                  )}
                </div>
                <span
                  style={{
                    marginLeft: "10px",
                    color: "#6b7280",
                    fontSize: "14px",
                  }}
                >
                  {eventTypeData?.owner || "User"}
                </span>
              </div>

              {/* Title as a separate block */}
              <h2
                style={{
                  color: "#111827",
                  fontSize: "20px",
                  fontWeight: "bold",
                  marginBottom: "10px",
                  marginTop: "0",
                }}
              >
                {eventTypeData?.title}
              </h2>

              {/* Description as a separate block */}
              <div
                style={{
                  color: "#6b7280",
                  fontSize: "14px",
                  lineHeight: "1.6",
                }}
                dangerouslySetInnerHTML={{ __html: eventTypeData?.description }}
              />
            </div>

            {/* Team Member Selection - Only show for team events */}
            {isTeamEvent && teamMembers.length > 0 && (
              <div style={styles.teamMemberSection}>
                <div style={styles.teamMemberSectionTitle}>
                  Select a Team Member
                </div>
                <div style={styles.teamMemberHelperText}>
                  Choose who you'd like to meet with (optional)
                </div>
                <div style={styles.teamMemberGrid}>
                  {/* "Any Available" option */}
                  <div
                    onClick={() => setSelectedTeamMember(null)}
                    style={{
                      ...styles.teamMemberCard,
                      ...(selectedTeamMember === null
                        ? styles.teamMemberCardSelected
                        : {}),
                    }}
                  >
                    <div style={styles.anyAvailableIcon}>👥</div>
                    <span style={styles.teamMemberName}>Any Available</span>
                  </div>

                  {/* Individual team members */}
                  {teamMembers.map((member) => (
                    <div
                      key={member.id}
                      onClick={() => setSelectedTeamMember(member)}
                      style={{
                        ...styles.teamMemberCard,
                        ...(selectedTeamMember?.id === member.id
                          ? styles.teamMemberCardSelected
                          : {}),
                      }}
                    >
                      {member.avatar ? (
                        <img
                          src={member.avatar}
                          alt={member.name}
                          style={styles.memberAvatar}
                        />
                      ) : (
                        <div style={styles.memberAvatarPlaceholder}>
                          {member.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <span style={styles.teamMemberName}>{member.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Details container */}
            <div style={{ marginTop: "24px" }}>
              {eventTypeData?.advanced?.requiresConfirmation?.enabled && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "16px",
                    fontSize: "14px",
                    color: "#4b5563",
                  }}
                >
                  <Check
                    size={16}
                    style={{ marginRight: "8px", color: "#6b7280" }}
                  />
                  <span>Requires confirmation</span>
                </div>
              )}

              {/* Duration display or selector */}
              {eventTypeData?.allowMultipleDurations &&
              eventTypeData?.availableDurations?.length > 0 ? (
                <div
                  style={{
                    marginBottom: "16px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "8px",
                      fontSize: "14px",
                      color: "#4b5563",
                    }}
                  >
                    <Clock
                      size={16}
                      style={{ marginRight: "8px", color: "#6b7280" }}
                    />
                    <span>Select Duration</span>
                  </div>
                  <div
                    style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}
                  >
                    {eventTypeData.availableDurations.map((dur) => (
                      <button
                        key={dur}
                        onClick={() => setSelectedDuration(dur)}
                        style={{
                          padding: "8px 16px",
                          borderRadius: "6px",
                          border:
                            selectedDuration === dur
                              ? "2px solid #3b82f6"
                              : "1px solid #d1d5db",
                          backgroundColor:
                            selectedDuration === dur ? "#eff6ff" : "#fff",
                          color:
                            selectedDuration === dur ? "#3b82f6" : "#374151",
                          fontSize: "14px",
                          fontWeight: selectedDuration === dur ? "600" : "400",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                        }}
                      >
                        {dur} min
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "16px",
                    fontSize: "14px",
                    color: "#4b5563",
                  }}
                >
                  <Clock
                    size={16}
                    style={{ marginRight: "8px", color: "#6b7280" }}
                  />
                  <span>{eventTypeData?.duration}m</span>
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "16px",
                  fontSize: "14px",
                  color: "#4b5563",
                }}
              >
                <Video
                  size={16}
                  style={{ marginRight: "8px", color: "#6b7280" }}
                />
                <span>Video Call</span>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "16px",
                  fontSize: "14px",
                  color: "#4b5563",
                }}
              >
                <Globe
                  size={16}
                  style={{ marginRight: "8px", color: "#6b7280" }}
                />
                <span>
                  {eventTypeData?.timezone ||
                    availabilityData?.timezone ||
                    "UTC"}
                </span>
              </div>
            </div>
          </div>

          {/* Main calendar section */}
          <div style={styles.main}>
            <div style={styles.headerRow}>
              <h2 style={styles.dateDisplay}>{selectedDate.format("ddd D")}</h2>
              <div style={{ display: "flex" }}>
                <div style={styles.viewToggle}>
                  {/* Show Month button if layout.month is true or layout is not configured */}
                  {eventTypeData?.advanced?.layout?.month !== false && (
                    <button
                      style={{
                        ...styles.viewButton,
                        ...(viewMode === "month"
                          ? styles.viewButtonActive
                          : {}),
                      }}
                      onClick={() => setViewMode("month")}
                    >
                      Month
                    </button>
                  )}
                  {/* Show Week button if layout.weekly is true or layout is not configured */}
                  {eventTypeData?.advanced?.layout?.weekly !== false && (
                    <button
                      style={{
                        ...styles.viewButton,
                        ...(viewMode === "week" ? styles.viewButtonActive : {}),
                      }}
                      onClick={() => setViewMode("week")}
                    >
                      Week
                    </button>
                  )}
                  {/* Show Day button if layout.column is true or layout is not configured */}
                  {eventTypeData?.advanced?.layout?.column !== false && (
                    <button
                      style={{
                        ...styles.viewButton,
                        ...(viewMode === "day" ? styles.viewButtonActive : {}),
                      }}
                      onClick={() => setViewMode("day")}
                    >
                      Day
                    </button>
                  )}
                </div>

                <div style={styles.timeFormatToggle}>
                  <button
                    style={{
                      ...styles.timeFormatButton,
                      ...(timeFormat === "12h"
                        ? styles.timeFormatButtonActive
                        : {}),
                    }}
                    onClick={() => setTimeFormat("12h")}
                  >
                    12h
                  </button>
                  <button
                    style={{
                      ...styles.timeFormatButton,
                      ...(timeFormat === "24h"
                        ? styles.timeFormatButtonActive
                        : {}),
                    }}
                    onClick={() => setTimeFormat("24h")}
                  >
                    24h
                  </button>
                </div>
              </div>
            </div>

            <div style={styles.calendarContainer}>
              {viewMode === "month" && renderMonthView()}
              {viewMode === "week" && renderWeekView()}
              {viewMode === "day" && renderDayView()}
            </div>
          </div>

          {/* Right sidebar with time slots */}
          <div style={styles.timeline}>
            <h3 style={styles.timelineHeader}>Available Times</h3>

            <div style={styles.timeSlotsContainer}>
              {availableSlots.length > 0 ? (
                <div style={styles.timeSlotsList}>
                  {availableSlots.map((slot, index) => (
                    <button
                      key={index}
                      style={{
                        ...styles.timeSlotButton,
                        ...(selectedTime === slot.timeStart
                          ? styles.timeSlotButtonSelected
                          : {}),
                      }}
                      onClick={() => selectTime(slot.timeStart)}
                    >
                      {slot.timeStart}
                    </button>
                  ))}
                </div>
              ) : (
                <div style={styles.noSlots}>
                  <p>No available slots</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div style={styles.footer}>
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.footerLink}
          >
            Powered by Instaconnect.io
          </a>
        </div>
      </div>

      {/* Booking Modal with Loading State */}
      {openModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "20px",
            overflow: "auto", // Allow scrolling on the overlay for very small screens
          }}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "8px",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
              width: "100%",
              maxWidth: "500px",
              maxHeight: "90vh", // Limit height to 90% of viewport
              overflow: "hidden",
              position: "relative", // Add this for absolute positioning of loading overlay
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Loading overlay */}
            {isSubmitting && (
              <div style={styles.loadingOverlay} className="loading-overlay">
                <div style={styles.loadingMessage}>
                  <div
                    style={styles.loadingSpinner}
                    className="booking-spinner"
                  ></div>
                  <p style={{ marginTop: "16px", color: "#4b5563" }}>
                    Creating your booking...
                  </p>
                </div>
              </div>
            )}

            <div
              style={{
                padding: "20px 24px",
                borderBottom: "1px solid #e5e7eb",
                flexShrink: 0, // Prevent header from shrinking
              }}
            >
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#111827",
                  margin: 0,
                }}
              >
                {successMessage ? "Booking Confirmed" : "Confirm Your Booking"}
              </h3>
              {renderTimer()}
            </div>

            <div
              style={{
                padding: "24px",
                overflowY: "auto", // Enable vertical scrolling for content
                flex: 1, // Take remaining space
              }}
            >
              {successMessage ? (
                <div style={{ textAlign: "center" }}>
                  <div style={styles.successIcon} className="success-icon">
                    <Check size={24} />
                  </div>
                  <div style={styles.formSuccess}>{successMessage}</div>
                  <p style={{ marginTop: "16px", color: "#4b5563" }}>
                    We've sent the confirmation details to your email.
                  </p>
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: "16px" }}>
                    <p style={{ fontSize: "15px", marginBottom: "8px" }}>
                      <strong>Event:</strong> {eventTypeData?.title}
                    </p>
                    <p style={{ fontSize: "15px", marginBottom: "8px" }}>
                      <strong>Date:</strong>{" "}
                      {selectedDate.format("dddd, MMMM D, YYYY")}
                    </p>
                    <p style={{ fontSize: "15px", marginBottom: "8px" }}>
                      <strong>Time:</strong> {selectedTime}
                    </p>
                    <p style={{ fontSize: "15px", marginBottom: "8px" }}>
                      <strong>Duration:</strong>{" "}
                      {eventTypeData?.allowMultipleDurations && selectedDuration
                        ? selectedDuration
                        : eventTypeData?.duration}{" "}
                      minutes
                    </p>
                    <p style={{ fontSize: "15px", marginBottom: "8px" }}>
                      <strong>Timezone:</strong>{" "}
                      {eventTypeData?.timezone || "UTC"}
                      {eventTypeData?.advanced?.lockTimezone && (
                        <span
                          style={{
                            fontSize: "12px",
                            color: "#6b7280",
                            marginLeft: "8px",
                          }}
                        >
                          (locked)
                        </span>
                      )}
                    </p>
                    {/* Show available seats if offerSeats is enabled and showAvailableSeats is true */}
                    {eventTypeData?.advanced?.offerSeats?.enabled &&
                      eventTypeData?.advanced?.offerSeats
                        ?.showAvailableSeats && (
                        <p
                          style={{
                            fontSize: "15px",
                            marginBottom: "8px",
                            color: "#059669",
                          }}
                        >
                          <strong>Available Seats:</strong>{" "}
                          {(() => {
                            const totalSeats =
                              eventTypeData.advanced.offerSeats.seats || 1;
                            const bookedSeats = existingBookings.filter(
                              (b) =>
                                b.timeStart.toLowerCase() ===
                                selectedTime.toLowerCase()
                            ).length;
                            const availableSeats = Math.max(
                              0,
                              totalSeats - bookedSeats
                            );
                            return `${availableSeats} of ${totalSeats}`;
                          })()}
                        </p>
                      )}
                    {/* Show requires confirmation notice */}
                    {eventTypeData?.advanced?.requiresConfirmation?.enabled && (
                      <div
                        style={{
                          marginTop: "12px",
                          padding: "8px 12px",
                          backgroundColor: "#fef3c7",
                          borderRadius: "4px",
                          border: "1px solid #f59e0b",
                        }}
                      >
                        <p
                          style={{
                            fontSize: "13px",
                            color: "#92400e",
                            margin: 0,
                          }}
                        >
                          ⚠️ This booking requires confirmation from the host
                        </p>
                      </div>
                    )}
                  </div>

                  <div style={{ marginBottom: "16px" }}>
                    <label style={styles.formInputLabel} htmlFor="name">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      style={styles.formInput}
                      required
                      disabled={isSubmitting}
                    />
                  </div>

                  <div style={{ marginBottom: "16px" }}>
                    <label style={styles.formInputLabel} htmlFor="email">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      style={styles.formInput}
                      required
                      disabled={isSubmitting}
                    />
                  </div>

                  <div style={{ marginBottom: "16px" }}>
                    <label style={styles.formInputLabel} htmlFor="notes">
                      Additional Notes *
                    </label>
                    <textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      style={styles.formTextarea}
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* Recurring booking options */}
                  {eventTypeData?.recurring?.enabled && (
                    <div
                      style={{
                        marginBottom: "16px",
                        padding: "16px",
                        backgroundColor: "#f0f9ff",
                        borderRadius: "8px",
                        border: "1px solid #0ea5e9",
                      }}
                    >
                      <label
                        style={{
                          ...styles.formInputLabel,
                          color: "#0369a1",
                          marginBottom: "12px",
                          display: "block",
                        }}
                      >
                        🔄 Recurring Booking
                      </label>
                      <p
                        style={{
                          fontSize: "13px",
                          color: "#64748b",
                          marginBottom: "12px",
                        }}
                      >
                        This event type supports recurring bookings. Choose how
                        many sessions you'd like to book.
                      </p>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          marginBottom: "12px",
                        }}
                      >
                        <label style={{ fontSize: "14px", color: "#334155" }}>
                          Number of sessions:
                        </label>
                        <input
                          type="number"
                          min="1"
                          max={eventTypeData.recurring.maxOccurrences || 12}
                          value={recurringOccurrences}
                          onChange={(e) =>
                            handleRecurringOccurrencesChange(
                              parseInt(e.target.value) || 1
                            )
                          }
                          style={{
                            ...styles.formInput,
                            width: "80px",
                            textAlign: "center",
                          }}
                          disabled={isSubmitting}
                        />
                        <span style={{ fontSize: "13px", color: "#64748b" }}>
                          (max: {eventTypeData.recurring.maxOccurrences || 12})
                        </span>
                      </div>

                      <p
                        style={{
                          fontSize: "13px",
                          color: "#64748b",
                          marginBottom: "8px",
                        }}
                      >
                        Repeats every {eventTypeData.recurring.frequency}{" "}
                        {eventTypeData.recurring.unit.toLowerCase()}(s)
                      </p>

                      {/* Show preview of recurring dates */}
                      {recurringOccurrences > 1 &&
                        recurringDates.length > 0 && (
                          <div
                            style={{
                              marginTop: "12px",
                              padding: "12px",
                              backgroundColor: "#ffffff",
                              borderRadius: "6px",
                              border: "1px solid #e2e8f0",
                            }}
                          >
                            <p
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: "#334155",
                                marginBottom: "8px",
                              }}
                            >
                              📅 Scheduled Dates Preview:
                            </p>
                            <div
                              style={{
                                maxHeight: "120px",
                                overflowY: "auto",
                                fontSize: "13px",
                                color: "#475569",
                              }}
                            >
                              {recurringDates.map((date, idx) => (
                                <div
                                  key={idx}
                                  style={{
                                    padding: "4px 0",
                                    borderBottom:
                                      idx < recurringDates.length - 1
                                        ? "1px solid #f1f5f9"
                                        : "none",
                                  }}
                                >
                                  {idx + 1}.{" "}
                                  {dayjs(date).format("dddd, MMMM D, YYYY")}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                    </div>
                  )}

                  {/* Render custom booking questions from advanced settings */}
                  {renderCustomBookingQuestions()}
                </>
              )}
            </div>

            <div
              style={{
                padding: "16px 24px",
                borderTop: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
                flexShrink: 0, // Prevent footer from shrinking
              }}
            >
              <button
                style={styles.modalButtonSecondary}
                onClick={() => {
                  setOpenModal(false);
                  if (successMessage) {
                    setSuccessMessage("");
                    setFormData({ name: "", email: "", notes: "" });
                    setCustomQuestionAnswers({});
                  }
                }}
                disabled={isSubmitting}
              >
                {successMessage ? "Close" : "Cancel"}
              </button>

              {!successMessage && (
                <button
                  style={{
                    ...styles.modalButtonPrimary,
                    ...(isSubmitting || !isFormValid ? styles.disabledButton : {}),
                  }}
                  onClick={handleSubmitBooking}
                  disabled={isSubmitting || !isFormValid}
                >
                  {isSubmitting ? "Submitting..." : "Confirm Booking"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Updated styles with animations */}
      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          
          @media (max-width: 768px) {
            .flexRow {
              flex-direction: column;
            }
            
            .sidebar, .main, .timeline {
              width: 100%;
              border-right: none;
              border-bottom: 1px solid #e5e7eb;
            }
          }

          /* Add loading animation for the spinner */
          .booking-spinner {
            width: 40px;
            height: 40px;
            border: 4px solid rgba(59, 130, 246, 0.1);
            border-top-color: #3b82f6;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }

          /* Add fade-in animation for the overlay */
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }

          .loading-overlay {
            animation: fadeIn 0.3s ease;
          }

          /* Add success checkmark animation */
          @keyframes scaleIn {
            0% { transform: scale(0); }
            50% { transform: scale(1.2); }
            100% { transform: scale(1); }
          }

          .success-icon {
            animation: scaleIn 0.5s ease;
          }
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.7; }
            100% { opacity: 1; }
          }
          
          .timer-warning {
            animation: pulse 1s infinite;
          }

          /* OTP Modal styles */
          .otp-modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.7);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 10000;
            animation: fadeIn 0.3s ease;
          }

          .otp-modal {
            background: white;
            border-radius: 12px;
            padding: 32px;
            max-width: 400px;
            width: 90%;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          }

          .otp-input {
            width: 100%;
            padding: 16px;
            font-size: 24px;
            text-align: center;
            letter-spacing: 8px;
            border: 2px solid #e5e7eb;
            border-radius: 8px;
            outline: none;
            transition: border-color 0.2s;
          }

          .otp-input:focus {
            border-color: #3b82f6;
          }

          .otp-input.error {
            border-color: #ef4444;
          }
        `}
      </style>

      {/* OTP Verification Modal */}
      {showOTPModal && (
        <div className="otp-modal-overlay">
          <div className="otp-modal">
            <h2
              style={{
                margin: "0 0 8px 0",
                fontSize: "24px",
                color: "#111827",
              }}
            >
              Verify Your Email
            </h2>
            <p
              style={{
                margin: "0 0 24px 0",
                color: "#6b7280",
                fontSize: "14px",
              }}
            >
              We've sent a 6-digit verification code to{" "}
              <strong>{pendingEmail}</strong>
            </p>

            <input
              type="text"
              className={`otp-input ${
                otpError && otpError.includes("Invalid") ? "error" : ""
              }`}
              placeholder="000000"
              maxLength={6}
              value={otpValue}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "");
                setOtpValue(val);
                if (otpError) setOtpError("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && otpValue.length === 6) {
                  handleVerifyOTP();
                }
              }}
              autoFocus
            />

            {otpError && (
              <p
                style={{
                  margin: "12px 0 0 0",
                  color: otpError.includes("sent") ? "#10b981" : "#ef4444",
                  fontSize: "14px",
                  textAlign: "center",
                }}
              >
                {otpError}
              </p>
            )}

            <p
              style={{
                margin: "16px 0",
                color: "#9ca3af",
                fontSize: "12px",
                textAlign: "center",
              }}
            >
              {attemptsRemaining} attempt{attemptsRemaining !== 1 ? "s" : ""}{" "}
              remaining • Code expires in 10 minutes
            </p>

            <button
              onClick={handleVerifyOTP}
              disabled={otpLoading || otpValue.length !== 6}
              style={{
                width: "100%",
                padding: "14px",
                backgroundColor:
                  otpLoading || otpValue.length !== 6 ? "#9ca3af" : "#3b82f6",
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontSize: "16px",
                fontWeight: "600",
                cursor:
                  otpLoading || otpValue.length !== 6
                    ? "not-allowed"
                    : "pointer",
                marginBottom: "12px",
              }}
            >
              {otpLoading ? "Verifying..." : "Verify & Confirm Booking"}
            </button>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <button
                onClick={handleResendOTP}
                disabled={otpLoading}
                style={{
                  background: "none",
                  border: "none",
                  color: "#3b82f6",
                  cursor: otpLoading ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  padding: "8px 0",
                }}
              >
                Resend Code
              </button>

              <button
                onClick={() => {
                  setShowOTPModal(false);
                  releaseTimeSlotLock();
                }}
                style={{
                  background: "none",
                  border: "none",
                  color: "#6b7280",
                  cursor: "pointer",
                  fontSize: "14px",
                  padding: "8px 0",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EventTypePreview;
